import ky from 'ky';
// The i18next singleton lib/i18n.ts initializes: read for the language
// formatCents formats in. Not lib/i18n itself, which bundles the locales.
import i18next from 'i18next';
// money.ts is a pure module (no imports): no cycle.
import { formatMoney } from '@/lib/money';
import { useAuthStore } from '@/store/auth';
import { toast } from '@/store/toast';
import { decodeJwtSub } from '@/lib/jwt';
import { appendAuthEvent, logoutWithReason } from '@/lib/auth-events';
import {
  attemptRefresh, classifyAuthFailure, failureDetail, httpStatusOf, isSessionEndingStatus,
  refreshWithOneRetry, retryOnce, singleFlight,
} from '@/lib/refreshSession';
// Value import of the terms-prompt entry point; termsPrompt only type-imports
// back from here, so there is no runtime cycle.
import { requestTermsAcceptance } from '@/store/termsPrompt';
// One-way import (homeRate.ts is a pure module) — no cycle.
import type { HomeRateType, HomeTouWindow, HomeRateTier } from '@/lib/homeRate';
// payoutCountry.ts only type-imports back from here: no runtime cycle.
import {
  createdInOtherCountry, isCountryRequiredError, payoutEntry, type PayoutCountry,
} from '@/lib/payoutCountry';
import type {
  ApiResponse, ApiError, LoginRequest, LoginResponse, PublicUser,
  PaginationMeta, AvatarStyle,
  FavoritesList, FavoriteToggleResponse, FavoriteCheckResponse,
  UserGamificationSummary, PointsHistoryEntry, PointsEventType,
  CouponPublic, CouponClaimPublic,
  StorePublicProduct,
  AnnouncementPublic,
  SupportedLocale,
  HomeCountry,
  MarketingCategory,
} from '@evi/shared';

const BASE_URL = import.meta.env['VITE_API_URL'] ?? '/api';

/** The API base the PWA calls — exported for the rare FULL-PAGE
 *  navigation to an API route (the Google OAuth kickoff at
 *  /auth/google), where the ky client can't be used because the
 *  redirect chain leaves and returns the page. Works in both shapes:
 *  an absolute VITE_API_URL or the relative /api dev-proxy path. */
export const API_BASE_URL = BASE_URL;

/** Public OCPP WebSocket base a homeowner programs into their charger via the
 *  Autel or Osell app. The charger appends its own identity (its serial) as the
 *  last path segment, so this is the base origin only, never a full per-charger
 *  URL. Overridable per deploy via VITE_OCPP_WS_BASE. The OCPP load balancer
 *  exposes 443/wss and 8080/plain-ws (budget home chargers often cannot speak
 *  TLS), so the staging default is the plain-ws listener; production sets
 *  VITE_OCPP_WS_BASE (for example wss://ocpp.evinitiative.com) at deploy.
 *
 *  Register-first (2026-10-02): a host now copies the per-charger URL the API
 *  builds (MyCharger.connection.url, from OCPP_PUBLIC_WS_URL), so Basic auth
 *  never goes out over plain ws. This base is only a display fallback for an
 *  API that predates that field. */
export const OCPP_WS_BASE: string =
  import.meta.env['VITE_OCPP_WS_BASE'] ?? 'ws://ocpp.stg.evinitiative.com:8080';

/** POST /auth/refresh through raw `ky`, so the afterResponse hook below
 *  never sees the refresh call's own 401. */
function postRefresh(signal?: AbortSignal): Promise<unknown> {
  return ky.post(`${BASE_URL}/auth/refresh`, { credentials: 'include', signal }).json();
}

function redirectToLogin(): void {
  // Path-prefix check covers /login + /login?next=... so an already-open
  // Login page is not reloaded onto itself.
  if (!window.location.pathname.startsWith('/login')) {
    window.location.href = '/login';
  }
}

/**
 * One refresh round, shared by every request that gets a 401 while it is
 * pending: N parallel 401s cost one POST and reach one decision (one sign-
 * out, one event, one redirect) instead of N. Resolves with the new access
 * token, or null when the caller should let its original 401 surface.
 *
 * Only a 401 or 403 from /auth/refresh ends the session. A network error,
 * timeout, 408, 429 or 5xx is retried once after 500ms and then the session
 * is KEPT: no clearAuth, no redirect. Those failures say nothing about the
 * cookie, and before this a rate-limit or deploy blip signed drivers out.
 */
const refreshSessionOnce = singleFlight(async (): Promise<string | null> => {
  const outcome = await refreshWithOneRetry(() => postRefresh());

  if (outcome.kind === 'success') {
    const newToken = outcome.token;

    // ── Defensive: token-vs-user sub validation ──
    // The refresh cookie can outlive a logout (e.g. if the
    // logout request never reached the server pre-Fix-C, or
    // a multi-tab/extension race). The server happily mints
    // a new access token from a stale cookie, and that token's
    // `sub` won't match the user object the local store
    // currently holds. Without this check, setAccessToken
    // would persist the mismatched token and the desync
    // banner would fire later. Catching the divergence here
    // turns silent corruption into a forced re-login at the
    // moment of the refresh.
    //
    // decodeJwtSub returns null on malformed tokens; we
    // only treat KNOWN-mismatched-sub as a desync. Null
    // tokenSub OR null storedUserId falls through to the
    // normal setAccessToken path so we don't block legit
    // refreshes on missing data.
    const tokenSub = decodeJwtSub(newToken);
    const storedUserId = useAuthStore.getState().user?._id;
    if (tokenSub && storedUserId && tokenSub !== storedUserId) {
      logoutWithReason('sub-mismatch', { tokenSub, storedUserId });
      toast('Session changed. Sign in to continue.', {
        tone: 'warn', durationMs: 4000,
      });
      redirectToLogin();
      return null;
    }

    useAuthStore.getState().setAccessToken(newToken);
    return newToken;
  }

  // The status and server message go in the ring buffer so the next
  // bounce names itself ("No refresh token", "Session has been revoked").
  const detail = failureDetail(outcome);
  if (outcome.kind === 'terminal') {
    logoutWithReason(detail.status === 403 ? 'refresh-failed-403' : 'refresh-failed-401', detail);
    redirectToLogin();
    return null;
  }
  appendAuthEvent({ reason: 'refresh-failed-transient', attempts: 2, ...detail });
  return null;
});

export const api = ky.create({
  prefixUrl: BASE_URL,
  credentials: 'include',
  timeout: 30_000,
  retry: {
    limit: 1,
    // No 401 here: the hook below already replays a request after a good
    // refresh, and a ky retry of a 401 the hook gave up on fired a second
    // refresh. GETs still retry once on a network error.
    statusCodes: [],
    methods: ['get'],
  },
  hooks: {
    beforeRequest: [
      (request) => {
        const token = useAuthStore.getState().accessToken;
        if (token) request.headers.set('Authorization', `Bearer ${token}`);
      },
    ],
    /**
     * 401 → shared refresh → replay. A refused refresh (401 or 403)
     * clears auth and redirects; any other failure keeps the session
     * and lets the original 401 surface (see refreshSessionOnce).
     *
     * Tightened from the prior shape that put clearAuth + redirect
     * inside the catch block only. The prior shape had three holes:
     *   HOLE 1 — refresh endpoint returns 200 with success:false
     *     (rare with current backend; defensive future-proofing).
     *     Old code: if-block doesn't enter, catch doesn't fire, hook
     *     returns undefined, caller sees original 401, React Query's
     *     .catch(() => 0) swallows → UI shows cached/zero data while
     *     auth is broken.
     *   HOLE 2 — refreshRes.data is null (malformed response).
     *     Old code: same as HOLE 1, falls through silently.
     *   HOLE 3 — already on /login when 401 fires.
     *     Old code: window.location.href = '/login' triggers a full
     *     reload of /login itself, which is wasteful and can race
     *     with React state during the reload.
     *
     * Fix: track refreshOk explicitly, route ALL non-success
     * outcomes through a unified cleanup path. Defensive-data
     * narrowing (attemptRefresh in refreshSession.ts) prevents HOLE 2.
     * Pathname guard prevents HOLE 3.
     *
     * Companion change: cross-tab storage listener in App.tsx
     * (subscribes to localStorage 'evi-web-auth' updates so a
     * logout in tab A propagates to tab B's in-memory state
     * before tab B's next API call hits this hook).
     */
    afterResponse: [
      async (request, _options, response): Promise<Response | void> => {
        if (response.status !== 401) return;

        // ── Loop protection: /auth/refresh's own 401 short-circuits ──
        // Currently the refresh call (postRefresh) uses raw `ky.post(...)`
        // and bypasses this hook entirely. This guard is defensive
        // future-proofing: if any caller later switches the refresh
        // call to `api.post('auth/refresh')`, the 401 would otherwise
        // recurse infinitely through this hook. Catch it here and
        // log+redirect once.
        if (request.url.includes('/auth/refresh')) {
          logoutWithReason('refresh-failed-401', {
            source: 'afterResponse-loop-guard',
            url: request.url,
          });
          if (!window.location.pathname.startsWith('/login')) {
            window.location.href = '/login';
          }
          return;
        }

        // Every 401 in this burst joins the same refresh round, which
        // also makes the sign-out decision once (refreshSessionOnce).
        const newToken = await refreshSessionOnce();
        if (!newToken) return;
        request.headers.set('Authorization', `Bearer ${newToken}`);
        return ky(request);
      },
    ],
  },
});

// ── Auth ─────────────────────────────────────────────────────────────────────

export async function login(body: LoginRequest): Promise<LoginResponse> {
  const res = await api.post('auth/login', { json: body }).json<ApiResponse<LoginResponse>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

export async function register(
  body: {
    name: string;
    email: string;
    password: string;
    tenant_slug?: string;
    join_code?: string;
    /** Fleet invite code (?fleet_code=, from the signed-out /join-fleet
     *  landing). Creates the account in the FLEET'S building so the
     *  post-auth join drain isn't refused cross-tenant; the drain, not
     *  register, does the actual fleet join. Loses to join_code. */
    fleet_code?: string;
    referral_code?: string;
    /** Raw home-whitelist join-link token (?homeToken=); resolves the
     *  pending invite server-side at account creation. */
    home_token?: string;
    /** Express marketing consent from the signup checkbox (unchecked by
     *  default). When true, the server records consent and awards the
     *  one-time marketing-opt-in reward. */
    marketing_opt_in?: boolean;
    /** Fleet email-invite JWT (?pendingToken=, the same token the join
     *  link carries). When it is valid for this email the new account is
     *  created in the fleet and the response carries `fleet_joined`. */
    fleet_invite_token?: string;
  },
): Promise<LoginResponse & FleetJoinedNotice> {
  const res = await api.post('auth/register', { json: body }).json<ApiResponse<LoginResponse & FleetJoinedNotice>>();
  return res.data;
}

/** Present on a register / magic-link-verify response when a fleet join
 *  was applied by that call (an email invite, or the fleet's email
 *  domain at the first verified sign-in). */
export interface FleetJoinedNotice {
  fleet_joined?: { fleet_name: string };
  /** A pending fleet join could not finish on this sign-in (the notice
   *  email could not be sent, the fleet is paused, ...) and is still
   *  waiting: the next emailed-link sign-in tries again. */
  fleet_join_deferred?: boolean;
}

// ── Building join (Session 8.b/8.c + per-Location join codes) ────

export interface PublicTenantSummary {
  _id: string;
  name: string;
  slug: string;
  display_name: string;
}

/** Per-Location lookup result. Used when the join code resolves to
 *  a Location (commercial-operator case) instead of a Tenant
 *  (multifamily 1:1 case). The PWA renders the Location's name +
 *  address as the headline and the parent tenant's name as the
 *  operator subtitle. */
export interface PublicLocationSummary {
  _id: string;
  name: string;
  subtitle: string | null;
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    coordinates?: { lat: number; lng: number };
  };
  tenant_id: string;
  tenant_name: string | null;
  tenant_slug: string | null;
}

/** Discriminated union for /join pre-auth lookup. Server-side
 *  Location-first / Tenant-second priority is mirrored here at
 *  the call site. Codegen's cross-collection guard ensures a code
 *  resolves to at most one entity, so order matters only for the
 *  404 path. */
export type PublicBuildingSummary =
  | { kind: 'location'; location: PublicLocationSummary }
  | { kind: 'tenant'; tenant: PublicTenantSummary };

/** Public Tenant lookup — no auth required. */
export async function lookupTenantByJoinCode(
  code: string,
): Promise<PublicTenantSummary | null> {
  const trimmed = code.trim().toUpperCase();
  if (trimmed.length !== 6) return null;
  try {
    const res = await api
      .get(`tenants/by-join-code/${encodeURIComponent(trimmed)}`)
      .json<ApiResponse<PublicTenantSummary>>();
    return res.data;
  } catch {
    return null;
  }
}

/** Public Location lookup — no auth required. Twin of the Tenant
 *  helper above; the per-Location codes commit added the endpoint. */
export async function lookupLocationByJoinCode(
  code: string,
): Promise<PublicLocationSummary | null> {
  const trimmed = code.trim().toUpperCase();
  if (trimmed.length !== 6) return null;
  try {
    const res = await api
      .get(`locations/by-join-code/${encodeURIComponent(trimmed)}`)
      .json<ApiResponse<PublicLocationSummary>>();
    return res.data;
  } catch {
    return null;
  }
}

/**
 * Unified building lookup. Hits Location first (the commercial-
 * operator case), falls back to Tenant on 404 (the multifamily
 * 1:1 case). Returns null when neither matches. The PWA's /join
 * page and switch-building modal use this to render the right
 * preview without caring which tier the code lives in.
 */
export async function lookupBuildingByJoinCode(
  code: string,
): Promise<PublicBuildingSummary | null> {
  const location = await lookupLocationByJoinCode(code);
  if (location) return { kind: 'location', location };
  const tenant = await lookupTenantByJoinCode(code);
  if (tenant) return { kind: 'tenant', tenant };
  return null;
}

export interface JoinBuildingResult {
  requires_relogin: boolean;
  new_tenant: { _id: string; name: string; slug: string };
  /** Populated when the join code resolved to a Location. Null
   *  when a Tenant-tier code was used (multifamily case). */
  new_location: { _id: string; name: string } | null;
}

/** Authenticated. Switches the caller's tenant. Server clears the
 *  refresh cookie + returns requires_relogin=true; caller drops
 *  local auth state and routes to /login.
 *
 *  agreement_version is the version string of the legal text the
 *  user accepted in the modal. The server compares it to its own
 *  CURRENT_JOIN_AGREEMENT_VERSION; mismatch returns 409 with code
 *  AGREEMENT_VERSION_OUTDATED so the modal can refresh and re-
 *  prompt before retrying. */
export async function joinBuildingByCode(
  code: string,
  agreement_version: string,
): Promise<JoinBuildingResult> {
  const trimmed = code.trim().toUpperCase();
  const res = await api
    .post('auth/join-building', { json: { code: trimmed, agreement_version } })
    .json<ApiResponse<JoinBuildingResult>>();
  return res.data;
}

export interface LeaveBuildingResult {
  /** Always true on success — the tenant always moves (back to the
   *  default tenant), so the session is always stale afterwards. */
  requires_relogin: boolean;
  new_tenant: { _id: string; name: string; slug: string };
}

/** Authenticated. Moves the caller back to the platform's default tenant.
 *  Server clears the driver refresh cookie; the caller routes through
 *  handleRequiresRelogin. 409 NOT_IN_BUILDING when already there, 409
 *  LEAVE_FLEET_FIRST while fleet-attributed (fleets are tenant-scoped). */
export async function leaveBuilding(): Promise<LeaveBuildingResult> {
  const res = await api
    .post('auth/leave-building')
    .json<ApiResponse<LeaveBuildingResult>>();
  return res.data;
}

// ── Fleet platform Phase 3b ─────────────────────────────────────────────────

export interface JoinFleetByCodeResult {
  fleet_id: string;
  fleet_name: string;
  /** True when the driver was already in this exact fleet (re-scan of
   *  their own QR, Back onto the link). 200, not 409. */
  already_member?: boolean;
}

export async function joinFleetByCode(code: string): Promise<JoinFleetByCodeResult> {
  const res = await api
    .post('fleets/join-by-code', { json: { code } })
    .json<ApiResponse<JoinFleetByCodeResult>>();
  return res.data;
}

export interface LeaveFleetResult {
  left: boolean;
  /** Null if the fleet doc was deactivated/removed underneath the driver. */
  fleet_name: string | null;
}

/** Authenticated. Driver-initiated exit from their current fleet — the
 *  self-serve twin of the admin unassign. 404 NOT_IN_FLEET when there is
 *  nothing to leave. Caller refreshes the auth store so fleet_id /
 *  current_mode drop out of the shell on the same render. */
export async function leaveFleet(): Promise<LeaveFleetResult> {
  const res = await api
    .post('fleets/leave')
    .json<ApiResponse<LeaveFleetResult>>();
  return res.data;
}

/** An existing account never joins here: the join is recorded and
 *  applies at its next verified sign-in (the emailed link), so it answers
 *  pending_verification whether or not the caller is signed in. */
export type AcceptFleetInviteResult =
  | { needs_registration: true; email: string; fleet_name: string }
  | { pending_verification: true; email: string; fleet_name: string }
  | { fleet_id: string; fleet_name: string; already_member: true };

export async function acceptFleetInvite(token: string): Promise<AcceptFleetInviteResult> {
  const res = await api
    .post('fleets/accept-invite', { json: { token } })
    .json<ApiResponse<AcceptFleetInviteResult>>();
  return res.data;
}

export interface FleetStatsResponse {
  range: 'this_month' | 'all_time';
  sessions_count: number;
  energy_kwh: number;
  duration_minutes: number;
  average_session_minutes: number;
  current_streak_days: number;
  cost_saved_estimate_cents: number;
  estimated_public_rate_cents_per_kwh: number;
  fleet_id: string | null;
  fleet_name: string | null;
}

export async function fetchFleetStats(
  range: 'this_month' | 'all_time',
): Promise<FleetStatsResponse> {
  const res = await api
    .get(`me/fleet-stats?range=${range}`)
    .json<ApiResponse<FleetStatsResponse>>();
  return res.data;
}

// ── Gamification — referral ─────────────────────────────────────────────────
// daily-checkin helper + interface removed in Phase 1 of the
// gamification rules update. Streak counter ticks on charging-
// session days now (see packages/gamification/src/session-points.ts);
// the app-mount POST that this helper drove is gone.

export interface ReferralPayload {
  code: string;
  totalReferrals: number;
  referrerBonus: number;
  refereeBonus: number;
}

export async function fetchReferral(): Promise<ReferralPayload> {
  const res = await api.get('gamification/referral').json<ApiResponse<ReferralPayload>>();
  return res.data;
}

// ── Redemption + wallet ────────────────────────────────────────────────────

export interface RedemptionConfig {
  minRedeemablePoints: number;
  pointsPerDollar: number;
}

export async function fetchRedemptionConfig(): Promise<RedemptionConfig> {
  const res = await api
    .get('gamification/redemption-config')
    .json<ApiResponse<RedemptionConfig>>();
  return res.data;
}

export interface WalletPayload {
  balanceCents: number;
  currency: string;
  pendingSessionCreditCents: number;
}

export async function fetchWallet(): Promise<WalletPayload> {
  const res = await api.get('gamification/wallet').json<ApiResponse<WalletPayload>>();
  return res.data;
}

// ── Wallet (cash balance + top-up) ───────────────────────────────────────────

export interface CashWalletPayload {
  /** The tenant-currency wallet (legacy single-wallet fields). */
  balanceCents: number;
  currency: string;
  minimumBalanceCents: number;
  minTopupCents: number;
  hasDefaultPaymentMethod: boolean;
  hasStripeCustomer: boolean;
  /** Every wallet the driver has, one per currency, USD first (2026-10-25).
   *  Absent from an older Api. */
  wallets?: Array<{ currency: string; balanceCents: number }>;
  /** The currencies this driver can top up ('USD', and 'CAD' for a driver
   *  who charges in Canada). Absent from an older Api (USD only). */
  topUpCurrencies?: string[];
  /** The driver's active make-good credits (2026-10-28), already inside the
   *  wallet balance of their currency: what is left of each, its expiry and
   *  its one-line reason (stored English text). Absent from an older Api. */
  credits?: WalletCredit[];
}

export interface WalletCredit {
  id: string;
  currency: string;
  remainingCents: number;
  /** ISO time. */
  expiresAt: string;
  reason: string;
}

export async function fetchCashWallet(): Promise<CashWalletPayload> {
  const res = await api.get('wallet').json<ApiResponse<CashWalletPayload>>();
  return res.data;
}

export interface TopupResult {
  requestedCents: number;
  chargedCents: number;
  bonusCreditCents: number;
  tierBonusCents?: number;
  totalCreditCents?: number;
  newBalanceCents: number;
  /** The wallet credited and the charge's currency. */
  currency: string;
  paymentIntentId: string;
  stripeStatus: string;
  appliedClaimId?: string;
}

export async function topupWallet(amountCents: number, paymentMethodId?: string, currency?: string): Promise<TopupResult> {
  const body: { amountCents: number; paymentMethodId?: string; currency?: string } = { amountCents };
  if (paymentMethodId) body.paymentMethodId = paymentMethodId;
  if (currency) body.currency = currency;
  const res = await api
    .post('wallet/topup', { json: body })
    .json<ApiResponse<TopupResult>>();
  return res.data;
}

// ── Pre-session start preview + payment selector ────────────────────
//
// The pre-session screen shows a payment selector + pre-auth copy
// BEFORE the driver taps Start. The PWA needs to know the gate
// verdict (mode / blocked reason), the pre-auth amount that will be
// held, and the lockout state to render the disabled subtitle. None
// of this is available without running the gate — hence the new
// preview endpoint, which mirrors the live gate without dispatching
// OCPP. Last4 / brand stay in /billing/payment-methods (unchanged).

export type StartPreviewMode = 'wallet' | 'pay_as_you_go';

/** Why the wallet can't be used before a start (@evi/shared walletOption). */
export type WalletUnavailableReason =
  | 'OUTSTANDING_BALANCE' | 'FLEET_SPONSORED' | 'UNSUPPORTED_CURRENCY'
  | 'CROSS_TENANT' | 'CURRENCY_MISMATCH' | 'FX_RATE_UNAVAILABLE' | 'BELOW_MINIMUM';

/** A USD wallet at a CAD charger, at the Bank of Canada rate the start would
 *  pin (2026-10-25T02:52:41Z ruling). valueCents is in sessionCurrency. */
export interface WalletConversionView {
  pair: 'USDCAD';
  walletCurrency: 'USD';
  sessionCurrency: 'CAD';
  rate: number;
  rateDate: string;
  source: 'bank_of_canada';
  valueCents: number;
}

/** The converted wallet debit on a session (Session.wallet_fx_debit). */
export interface SessionWalletFxDebit {
  pair: 'USDCAD';
  rate: number;
  rate_date: string;
  source: 'bank_of_canada';
  wallet_currency: 'USD';
  session_currency: 'CAD';
  /** USD taken from the wallet. */
  debit_cents: number;
  /** CAD of the session it paid. */
  paid_cents: number;
}

export interface StartPreviewResponse {
  ok: boolean;
  mode: StartPreviewMode | null;
  reason:
    | 'NO_PAYMENT_METHOD'
    | 'NO_STRIPE_CUSTOMER'
    | 'INSUFFICIENT_WALLET'
    | 'USER_NOT_FOUND'
    | 'PAYMENT_LOCKED'
    | 'OUTSTANDING_BALANCE'
    /** Fleet-sponsored start with no card path open. The fleet pays, so
     *  a wallet top-up can never unblock it — the banner offers "add a
     *  card" instead. */
    | 'FLEET_SPONSORED_NO_CARD_PATH'
    | null;
  hint: string | null;
  shortfallCents: number;
  preAuthAmountCents: number;
  preferredPaymentSource: 'card' | 'wallet' | null;
  card: {
    available: boolean;
    hasCardOnFile: boolean;
    lockedOut: boolean;
    lockoutUntil: string | null;
    /** The card a card-paid start holds against (the recorded default). Absent from an older Api. */
    paymentMethodId?: string | null;
  };
  wallet: {
    /** A wallet path exists here (usable now, or after a top-up in the
     *  price's currency). False for fleet, guest, outstanding balance. */
    available?: boolean;
    /** Cash plus counted session credit, in `currency`. */
    balanceCents: number;
    minimumBalanceCents: number;
    sufficient: boolean;
    /** The wallet's own currency (item 3), not the price's: a Canadian
     *  home's price is CAD while a wallet stays in its currency. Absent
     *  from an older Api (USD). */
    currency?: string;
    // ── The gate's wallet option (2026-10-25). Absent from an older Api. ──
    /** The start would take wallet mode for a driver who picks the wallet. */
    usable?: boolean;
    /** Why not, when not usable. */
    reason?: WalletUnavailableReason | null;
    /** The wallet's cash, in `currency`. */
    cashCents?: number;
    /** The price's currency. */
    sessionCurrency?: string;
    /** The operator minimum, in the price's currency. */
    requiredCents?: number;
    /** What to add to the wallet `topUpCurrency` to make it usable (the
     *  price's currency, or USD for a converted USD wallet). */
    shortfallCents?: number;
    /** The wallet to top up to make it usable, or null. */
    topUpCurrency?: string | null;
    wallets?: Array<{ currency: string; balanceCents: number }>;
    /** A USD wallet at a CAD charger and its value there. Absent or null
     *  otherwise, and from an older Api. */
    conversion?: WalletConversionView | null;
  };
  /** Resolved access mode for THIS user × charger pair. 'guest' for
   *  cross-tenant sessions (server-side wired by commit 4 of the
   *  third-party-charger-access ticket). 'tenant' for own-tenant
   *  sessions. Optional for back-compat with serializations that
   *  predate it. */
  accessMode?: 'tenant' | 'guest';
  /** Guest pricing markup (basis points) for guest sessions; null
   *  on tenant sessions. */
  guestMarkupBps?: number | null;
  /** The tariff that will actually bill this caller (Home Guest for
   *  paying guests at resident-managed chargers; the charger's own
   *  otherwise), in PublicTariff shape. Preferred by the pre-session
   *  rate row over the tenant-scoped /tariffs/:id fetch, which 404s
   *  for every cross-tenant guest. Optional for back-compat. */
  tariff?: PublicTariff | null;
  /** canGuestUseCharger verdict for guest sessions. Carries the
   *  reason (GUEST_ACCESS_DISABLED / OUTSIDE_GUEST_WINDOW / ALLOWED)
   *  and the next opening moment for OUTSIDE_GUEST_WINDOW so the PWA
   *  can render "available again at …" copy. Null on tenant
   *  sessions. */
  guestAccess?: {
    allowed: boolean;
    reason: 'ALLOWED' | 'GUEST_ACCESS_DISABLED' | 'OUTSIDE_GUEST_WINDOW';
    nextOpenAt?: string;
    /** Why GUEST_ACCESS_DISABLED, when known (item 6). 'PRICE_NOT_SET':
     *  a resident's charger open to guests with no guest price. The
     *  reason stays GUEST_ACCESS_DISABLED so older windows still block.
     *  Absent otherwise, and from an older Api. */
    detail?: 'PRICE_NOT_SET' | 'PRIVATE_CHARGER';
  } | null;
  /** The private switch (Warren, 2026-10-22): a private charger this
   *  driver may not start (not on its pass list, not its host, not EVI
   *  staff). guestAccess then carries GUEST_ACCESS_DISABLED with detail
   *  PRIVATE_CHARGER, so an older window blocks too. Absent from an older Api. */
  privateLocked?: boolean;
  /** EVI's connection fee this start would pin, in cents (Tier 2).
   *  Kept by EVI, never the host's. 0 when the session cannot bill (a
   *  free charger stays free). The server also rewrites
   *  `tariff.rates.flat_fee` to this same number, so a surface must
   *  read ONE of them, never add both. Optional for back-compat with a
   *  response that predates the field. */
  connectionFeeCents?: number;
}

export async function fetchStartPreview(
  chargerIdentity: string,
): Promise<StartPreviewResponse> {
  const res = await api
    .get(`chargers/${chargerIdentity}/start-preview`)
    .json<ApiResponse<StartPreviewResponse>>();
  return res.data;
}

export async function setPaymentSource(source: 'card' | 'wallet'): Promise<void> {
  // 204 No Content. ky's .json() chokes on empty bodies; raw call is
  // sufficient because we only care about success/throw.
  await api.post('sessions/payment-selector', { json: { source } });
}

// Fleet Mode Phase 3 — driver-shell mode selector. Posts to
// PATCH /me/mode and returns the new mode. The caller is responsible
// for mutating the auth-store user object (optimistic) and rolling
// back on rejection; useCurrentMode wraps both halves.
export async function setCurrentMode(
  mode: 'fleet' | 'public',
): Promise<{ mode: 'fleet' | 'public' }> {
  const res = await api
    .patch('me/mode', { json: { mode } })
    .json<ApiResponse<{ mode: 'fleet' | 'public' }>>();
  return res.data;
}

// Note: `setDefaultPaymentMethod` lives below in the existing
// payment-methods section — it now also mirrors the choice onto
// `User.default_stripe_payment_method_id` server-side so the session-
// start wallet gate can read it without a Stripe round-trip.

// ── Friends ─────────────────────────────────────────────────────────────────

export type FriendshipStatus = 'pending' | 'accepted' | 'declined' | 'blocked';

export interface FriendCard {
  friendshipId: string;
  direction: 'incoming' | 'outgoing';
  status: FriendshipStatus;
  requestedAt?: string;
  acceptedAt?: string;
  user: {
    id: string;
    name?: string;
    username?: string | null;
    avatarStyle?: string | null;
    avatarSeed?: string | null;
    equippedSkin?: string | null;
  };
  stats: {
    totalPoints: number;
    currentRank: string | null;
    totalKwh: number;
    streakDays: number;
    /** Weekly charging streak (consecutive ISO weeks) — distinct
     *  from streakDays, the daily streak. */
    streakWeeks: number;
  };
}

export interface FriendsList {
  accepted: FriendCard[];
  incoming: FriendCard[];
  outgoing: FriendCard[];
}

export interface FriendSearchResult {
  id: string;
  name?: string;
  username?: string | null;
  avatarStyle?: string | null;
  avatarSeed?: string | null;
  referralCode?: string | null;
  friendshipStatus: FriendshipStatus | null;
}

export async function fetchFriends(): Promise<FriendsList> {
  const res = await api.get('friends').json<ApiResponse<FriendsList>>();
  return res.data;
}

export async function searchFriends(q: string): Promise<FriendSearchResult[]> {
  const res = await api
    .get('friends/search', { searchParams: { q } })
    .json<ApiResponse<FriendSearchResult[]>>();
  return res.data;
}

export async function requestFriend(
  body: { recipientUserId?: string; recipientReferralCode?: string; recipientEmail?: string },
): Promise<{ friendshipId: string; status: FriendshipStatus } | { sent: true }> {
  // Email requests get the enumeration-resistant neutral shape ({ sent: true }),
  // identical whether or not the email matched; id / referral keep the friendship
  // shape. The add-by-email caller ignores the body (it shows a neutral message).
  const res = await api
    .post('friends/request', { json: body })
    .json<ApiResponse<{ friendshipId: string; status: FriendshipStatus } | { sent: true }>>();
  return res.data;
}

export async function acceptFriend(id: string): Promise<void> {
  await api.post(`friends/accept/${id}`);
}

/** What POST /friends/invite/accept reports for a friend link's code. Every refusal reads 'unavailable'. */
export interface FriendInviteAcceptResult {
  status: 'created' | 'accepted_pending' | 'already_friends' | 'self' | 'unavailable';
  friend: { id: string; name: string | null } | null;
}

/** The signed-in driver came in through a friend link: make them and the sharer friends (both are notified). */
export async function acceptFriendInvite(code: string): Promise<FriendInviteAcceptResult> {
  const res = await api
    .post('friends/invite/accept', { json: { code } })
    .json<ApiResponse<FriendInviteAcceptResult>>();
  return res.data;
}
export async function declineFriend(id: string): Promise<void> {
  await api.post(`friends/decline/${id}`);
}
export async function blockUser(userId: string): Promise<void> {
  await api.post(`friends/block/${userId}`);
}
export async function removeFriend(id: string): Promise<void> {
  await api.delete(`friends/${id}`);
}

/** Friend detail payload — used by the per-friend detail page.
 *  Backend gates this on accepted-friendship status; 404 if the
 *  caller and target aren't friends (privacy floor against
 *  direct-URL access). Stats are deliberately curated to social
 *  signals; transactional / location-specific data isn't exposed. */
export interface FriendBadgeShowcase {
  badgeId: string;
  name: string;
  description: string;
  rarity: string;
  iconName: string | null;
  colorTier: number | null;
  earnedAt: string;
}
export interface FriendDetail {
  friendshipId: string;
  friendsSince: string | null;
  user: {
    id: string;
    name: string;
    username: string | null;
    avatarStyle: string | null;
    avatarSeed: string | null;
    equippedSkin: string | null;
  };
  stats: {
    totalPoints: number;
    currentRank: string | null;
    totalKwh: number;
    totalSessions: number;
    streakDays: number;
    /** Weekly charging streak (consecutive ISO weeks). */
    streakWeeks: number;
    badgeCount: number;
  };
  topBadges: FriendBadgeShowcase[];
  mutualFriendsCount: number;
  /** Friendship (pair) streak — the SHARED streak between the viewer
   *  and this friend, distinct from stats.streakWeeks (the friend's
   *  own solo streak). `live` mirrors the server-side check that
   *  raises the pair's gift cap. Optional: absent on responses from
   *  servers that pre-date the field. */
  pairStreak?: { currentWeeks: number; live: boolean };
}

export async function fetchFriendDetail(userId: string): Promise<FriendDetail> {
  const res = await api.get(`friends/${userId}`).json<ApiResponse<{
    friendshipId: string;
    friendsSince: string | null;
    user: FriendDetail['user'];
    stats: FriendDetail['stats'];
    topBadges: Array<{
      badge_id: string;
      name: string;
      description: string;
      rarity: string;
      icon_name: string | null;
      color_tier: number | null;
      earned_at: string;
    }>;
    mutualFriendsCount: number;
    pairStreak?: { currentWeeks: number; live: boolean };
  }>>();
  // snake → camel for the badge entries; keep the rest of the
  // payload's existing camelCase shape.
  return {
    friendshipId: res.data.friendshipId,
    friendsSince: res.data.friendsSince,
    user: res.data.user,
    stats: res.data.stats,
    topBadges: res.data.topBadges.map((b) => ({
      badgeId: b.badge_id,
      name: b.name,
      description: b.description,
      rarity: b.rarity,
      iconName: b.icon_name,
      colorTier: b.color_tier,
      earnedAt: b.earned_at,
    })),
    mutualFriendsCount: res.data.mutualFriendsCount,
    ...(res.data.pairStreak ? { pairStreak: res.data.pairStreak } : {}),
  };
}

// ── Notifications ───────────────────────────────────────────────────────────
//
// PWA reads from the existing /admin/notifications/* endpoints. The
// route prefix is admin-shaped historically (Phase B shipped them
// under /admin/) but the handlers gate via target_resolver, not via
// admin-role permission — any authenticated user gets back only the
// notifications targeted at them. No /me/notifications mirror is
// needed; the work would be a copy-paste of the admin handlers with
// no semantic divergence.

export interface NotificationView {
  _id: string;
  type: string;
  title: string;
  body: string;
  link: string | null;
  metadata: Record<string, unknown>;
  /** First occurrence. Never moves. */
  created_at: string;
  /** Most recent occurrence. Equals created_at until the same thing
   *  happens again and the server bumps the row instead of adding a
   *  sibling (a flapping charger). Show THIS as the row's time. */
  last_occurred_at: string;
  /** 1, or how many times a coalesced row has fired. Render "×N"
   *  beside the time when it is above 1. */
  occurrence_count: number;
  resolved_at: string | null;
  read_at: string | null;
  dismissed_at: string | null;
  target_type: 'tenant' | 'user' | 'role';
  /** Which bell tab the row belongs to — decided server-side by the
   *  notification registry (Money is activity; offers and rewards are news). */
  tab: 'activity' | 'news';
}

export async function fetchNotifications(opts?: {
  unread_only?: boolean;
  limit?: number;
}): Promise<NotificationView[]> {
  const searchParams: Record<string, string> = {};
  if (opts?.unread_only) searchParams['unread_only'] = 'true';
  if (typeof opts?.limit === 'number') searchParams['limit'] = String(opts.limit);
  const res = await api
    .get('admin/notifications', { searchParams })
    .json<ApiResponse<NotificationView[]>>();
  return res.data;
}

export async function fetchNotificationsUnreadCount(): Promise<number> {
  const res = await api
    .get('admin/notifications/unread-count')
    .json<ApiResponse<{ count: number }>>();
  return res.data.count;
}

export async function markNotificationRead(id: string): Promise<void> {
  await api.post(`admin/notifications/${id}/read`);
}

export async function markNotificationDismissed(id: string): Promise<void> {
  await api.post(`admin/notifications/${id}/dismiss`);
}

// ── Streaks ─────────────────────────────────────────────────────────────────

export interface StreakType {
  code: string;
  name: string;
  description: string;
  windowDays: number;
  threshold: number;
  metric: 'sessions' | 'kwh' | 'unique_stations' | 'avg_power_kw';
  enrolled: boolean;
  currentWeekCount: number;
  longestWeekCount: number;
  lastSuccessfulWindowEnd: string | null;
  freezeOfferedAt: string | null;
  lastFreezeAt: string | null;
}

export interface StreaksPayload {
  types: StreakType[];
  freezeCostPoints: number;
  freezeCooldownDays: number;
  weeklyReward: number;
  fourWeekBonus: number;
}

export async function fetchStreaks(): Promise<StreaksPayload> {
  const res = await api.get('streaks').json<ApiResponse<StreaksPayload>>();
  return res.data;
}

export async function enrollStreak(code: string): Promise<void> {
  await api.post(`streaks/${code}/enroll`);
}
export async function unenrollStreak(code: string): Promise<void> {
  await api.post(`streaks/${code}/unenroll`);
}
export async function freezeStreak(code: string): Promise<{
  frozen: boolean; costPoints: number; newPointsBalance?: number;
}> {
  const res = await api
    .post(`streaks/${code}/freeze`)
    .json<ApiResponse<{ frozen: boolean; costPoints: number; newPointsBalance?: number }>>();
  return res.data;
}

// ── Challenges ──────────────────────────────────────────────────────────────

export interface ChallengeCard {
  id: string;
  code: string;
  name: string;
  description: string;
  metric: 'kwh' | 'sessions' | 'unique_stations';
  threshold: number;
  currentValue: number;
  progressPct: number;
  rewardPoints: number;
  rewardBadgeCode: string | null;
  weekStart: string;
  weekEnd: string;
  status: 'active' | 'completed' | 'expired';
  completedAt: string | null;
  rewardAwarded: boolean;
}

export interface ChallengePoolEntry {
  code: string;
  name: string;
  description: string;
  metric: ChallengeCard['metric'];
  threshold: number;
  rewardPoints: number;
}

export interface ActiveChallengePayload {
  active: ChallengeCard;
  pool: ChallengePoolEntry[];
}

export async function fetchActiveChallenge(): Promise<ActiveChallengePayload> {
  const res = await api.get('challenges/active').json<ApiResponse<ActiveChallengePayload>>();
  return res.data;
}
export async function fetchChallengeHistory(limit = 8): Promise<ChallengeCard[]> {
  const res = await api
    .get('challenges/history', { searchParams: { limit: String(limit) } })
    .json<ApiResponse<ChallengeCard[]>>();
  return res.data;
}

// ── Unlocks ─────────────────────────────────────────────────────────────────

export type ThemeUnlockKind =
  | 'default'
  | 'kwh_milestone'
  | 'session_milestone'
  | 'fleet_only'
  | 'admin_only';

export interface UnlockCard {
  code: string;
  name: string;
  description: string;
  category: 'skin' | 'priority_booking' | 'lounge_access';
  pricePoints: number;
  metadata: Record<string, unknown>;
  owned: boolean;
  purchasedAt: string | null;
  active: boolean;
  tierMinimum: string | null;
  tierEligible: boolean;
  // Theme milestones — only set on skin rows. Drives the picker's
  // progress copy + lock badge.
  unlockCriteriaKind: ThemeUnlockKind | null;
  unlockCriteriaThreshold: number | null;
  /** 0-100, only for kwh/session milestone kinds. Null otherwise. */
  progressPercent: number | null;
}

export interface UnlocksPayload {
  unlocks: UnlockCard[];
  activeSkinCode: string | null;
  totalPoints: number;
  currentTier: string;
  userTotalKwh: number;
  userTotalSessions: number;
  isFleetMember: boolean;
}

export interface ActiveSkinPayload {
  skin: {
    code: string;
    name: string;
    metadata: Record<string, unknown>;
  } | null;
}

export async function fetchUnlocks(): Promise<UnlocksPayload> {
  const res = await api.get('unlocks').json<ApiResponse<UnlocksPayload>>();
  return res.data;
}
export async function fetchActiveSkin(): Promise<ActiveSkinPayload> {
  const res = await api.get('unlocks/active-skin').json<ApiResponse<ActiveSkinPayload>>();
  return res.data;
}
// Phase: theme milestones — points-trade for skins is deprecated.
// Skins now unlock via charging milestones (kwh/session thresholds)
// or fleet attribution (Phase 3a, unchanged). The server route
// /unlocks/:code/purchase still exists for non-skin unlocks
// (priority_booking, lounge_access) but the PWA no longer ships a
// purchase affordance for skins. The `purchaseUnlock` helper was
// removed to prevent stale callers; if a future commit needs to
// purchase a non-skin unlock it can re-add a category-specific
// helper. See docs/operations/theme-milestones.md (if/when written)
// for the migration narrative.
export async function setActiveSkin(code: string | null): Promise<void> {
  await api.post('user/active-skin', { json: { code } });
}

/**
 * Persist a theme-family selection (e.g. 'default', 'luce') to the
 * driver's `recent_themes` array. Skin selections should use
 * `setActiveSkin` instead — it already writes recents as part of
 * the same round-trip. Family selection stays localStorage-backed
 * for the actual preference; this endpoint only records recency so
 * the picker's inline list includes family picks.
 */
export async function recordRecentTheme(code: string): Promise<void> {
  await api.post('user/recent-theme', { json: { code } });
}

// ── Gamification settings (read-only for drivers) ───────────────────────────
//
// The reward-points settings endpoint is requireAuth (not requireAdmin) —
// drivers can read it for UX surfaces that need to show earning rates
// (e.g. the ThemesPage "how to earn" sheet). Admin-only mutate-path is
// a separate endpoint with requireAdmin; we don't expose that client.

export interface RewardPointsSettings {
  pointsPerKwh: number;
  sessionCompletionBonus: number;
  referralBonusReferrer: number;
  referralBonusReferee: number;
  streakWeeklyReward: number;
  streakFourWeekBonus: number;
  minRedeemablePoints: number;
  pointsPerDollar: number;
  [extra: string]: unknown; // the server returns more fields we don't currently surface
}

export async function fetchRewardPointsSettings(): Promise<RewardPointsSettings> {
  const res = await api.get('settings/reward-points').json<ApiResponse<{
    value: RewardPointsSettings;
    defaults: RewardPointsSettings;
  }>>();
  return res.data.value;
}

// ── Feed ────────────────────────────────────────────────────────────────────

export type FeedEventType =
  | 'BADGE_EARNED'
  | 'STREAK_MILESTONE'
  | 'STREAK_AT_RISK'
  | 'CHALLENGE_COMPLETED'
  | 'RANK_UP'
  | 'FRIEND_ACCEPTED'
  | 'UNLOCK_PURCHASED';

export interface FeedEventCard {
  id: string;
  userId: string;
  isSelf: boolean;
  type: FeedEventType;
  payload: Record<string, unknown>;
  visibility: 'public' | 'friends_only' | 'private';
  author: {
    displayName: string;
    username: string | null;
    avatarStyle: string | null;
    avatarSeed: string | null;
    activeSkinCode: string | null;
  } | null;
  createdAt: string;
}

export interface FeedPage {
  events: FeedEventCard[];
  hasMore: boolean;
  nextBefore: string | null;
}

export async function fetchFeed(params?: { limit?: number; before?: string }): Promise<FeedPage> {
  const sp = new URLSearchParams();
  if (params?.limit) sp.set('limit', String(params.limit));
  if (params?.before) sp.set('before', params.before);
  const res = await api.get('feed', { searchParams: sp }).json<ApiResponse<FeedPage>>();
  return res.data;
}

// ── Gamification history (cursor-paginated) ─────────────────────────────────

/**
 * Cursor-paginated reward history. Drives the RewardsHistoryPage's
 * "Reward history" section; replaces the prior "fetch the same 20
 * rows from /user-summary" hack so drivers with > 20 events can
 * page through their full ledger.
 *
 * Cursor is opaque — server-encoded base64url JSON. Caller passes
 * the next_cursor verbatim from the previous response. Null
 * next_cursor signals end-of-feed.
 *
 * Page size: default 50, max 100 (server-enforced).
 */
export interface GamificationHistoryPage {
  entries: PointsHistoryEntry[];
  next_cursor: string | null;
}

export async function fetchGamificationHistory(
  params?: { limit?: number; before?: string },
): Promise<GamificationHistoryPage> {
  const sp = new URLSearchParams();
  if (params?.limit) sp.set('limit', String(params.limit));
  if (params?.before) sp.set('before', params.before);
  const res = await api
    .get('gamification/history', { searchParams: sp })
    .json<ApiResponse<GamificationHistoryPage>>();
  return res.data;
}

// ── Leaderboard ─────────────────────────────────────────────────────────────

export interface LeaderboardEntry {
  userId: string;
  displayName: string;
  avatarStyle: string | null;
  avatarSeed: string | null;
  kwh: number;
  sessions: number;
  pointsEarned: number;
  rank: number;
  isSelf: boolean;
}

export interface LeaderboardPayload {
  weekStart: string;
  entries: LeaderboardEntry[];
  selfRank: number | null;
}

export async function fetchFriendsLeaderboard(): Promise<LeaderboardPayload> {
  const res = await api
    .get('leaderboard/friends', { searchParams: { scope: 'current_week' } })
    .json<ApiResponse<LeaderboardPayload>>();
  return res.data;
}

// ── Game leaderboard ────────────────────────────────────────────────────────
// Game points are NOT EVI Points — bragging rights only, no
// conversion path (see GameProfileModel's firewall comment).

export interface GameLeaderboardEntry {
  userId: string;
  displayName: string;
  avatarStyle: string | null;
  avatarSeed: string | null;
  totalPoints: number;
  /** Per-game points, keyed by game ns; zero-point games omitted.
   *  The meta line renders each row's two strongest games from this. */
  gamePoints: Record<string, number>;
  rank: number;
  isSelf: boolean;
}

export interface GameLeaderboardPayload {
  entries: GameLeaderboardEntry[];
  selfRank: number | null;
}

export async function fetchGameLeaderboard(): Promise<GameLeaderboardPayload> {
  const res = await api
    .get('games/leaderboard/friends')
    .json<ApiResponse<GameLeaderboardPayload>>();
  return res.data;
}

/**
 * Fire-and-forget sync of a game's profile blob to the server after
 * the game saves. The server clamps and merges monotonically, so
 * sending the whole opaque blob is safe; errors are swallowed —
 * gameplay must never surface a sync failure.
 *
 * `context` tells the server where the overlay was mounted so the
 * GamePointsEvent ranked flag can be decided: 'charging' from a live
 * session's game overlay, 'arcade' from /arcade. The server treats
 * it as a claim, not a fact — a 'charging' claim is corroborated
 * against a live session before any event ranks.
 */
export function submitGameProfile(
  game: string,
  profile: unknown,
  context: 'charging' | 'arcade',
): void {
  void api
    .post('games/profile', { json: { game, profile, context } })
    .catch(() => { /* offline / auth blip — next save retries */ });
}

export interface RedeemResult {
  ledgerEntryId?: string;
  pointsRedeemed: number;
  dollarsCredited: number;
  centsCredited: number;
  newPointsBalance?: number;
  walletBalanceCents?: number;
  walletCurrency?: string;
  pendingSessionCreditCents?: number;
  willApplyToNextSession?: boolean;
  idempotent: boolean;
  target: 'wallet' | 'session_credit';
}

/**
 * Burn N points → wallet credit OR pending session credit.
 *
 * `idempotencyKey` (optional): if provided, the server will treat
 * repeat calls with the same key as no-ops. Useful for retrying
 * after a network blip; without it, every call is a fresh write.
 */
export async function redeemPoints(
  body: { points: number; target: 'wallet' | 'session_credit' },
  idempotencyKey?: string,
): Promise<RedeemResult> {
  const headers: Record<string, string> = {};
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
  const res = await api
    .post('gamification/redeem', { json: body, headers })
    .json<ApiResponse<RedeemResult>>();
  return res.data;
}

export interface GiftPointsResult {
  pointsGifted: number;
  /** Giver's total_points after the debit; null on an exotic
   *  engine path that omitted it. */
  giverBalance: number | null;
  recipientUserId: string;
  recipientUsername: string;
  /** True when the server deduped this call against a prior gift
   *  with the same Idempotency-Key (a retry, not an error). */
  idempotent: boolean;
}

/**
 * Gift points to a confirmed friend. The giver's balance is debited
 * and the friend's credited; the friend's rank never moves (gifts
 * credit spendable points only). Server enforces the accepted-
 * friendship gate, the per-recipient monthly cap, and the balance.
 *
 * `idempotencyKey` (optional but encouraged): repeat calls with the
 * same key collapse to one gift — safe to retry after a network
 * blip, and a retry also repairs a half-applied pair server-side.
 */
export async function giftPoints(
  body: { recipientUserId: string; points: number },
  idempotencyKey?: string,
): Promise<GiftPointsResult> {
  const headers: Record<string, string> = {};
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
  const res = await api
    .post('gamification/gift', { json: body, headers })
    .json<ApiResponse<GiftPointsResult>>();
  return res.data;
}

export async function requestMagicLink(email: string): Promise<{ message: string }> {
  const res = await api.post('auth/magic-link', { json: { email } }).json<ApiResponse<{ message: string }>>();
  return res.data;
}

export async function verifyMagicLink(email: string, token: string): Promise<LoginResponse & FleetJoinedNotice> {
  // Raw `ky` (not the `api` instance) so the afterResponse 401 interceptor never
  // engages: a failed magic-verify — an expired/used token (401) or an admin
  // account on the driver PWA (403) — must surface its own honest copy on the
  // verify page, NOT be hijacked into a silent refresh + redirect-to-login loop
  // (the reported bug). `credentials: 'include'` so the success path still
  // stores the httpOnly refresh cookie.
  let res: ApiResponse<LoginResponse & FleetJoinedNotice>;
  try {
    res = await ky
      .post(`${BASE_URL}/auth/magic-link/verify`, { json: { email, token }, credentials: 'include' })
      .json<ApiResponse<LoginResponse & FleetJoinedNotice>>();
  } catch (err) {
    // ky throws HTTPError on 4xx/5xx — surface the server's structured message.
    const resp = (err as { response?: Response }).response;
    let message = 'That sign-in link was already used or expired. Request a fresh one.';
    if (resp) {
      const body = (await resp.json().catch(() => null)) as ApiError | null;
      if (body?.error?.message) message = body.error.message;
    }
    throw new Error(message);
  }
  if (!res.success) {
    throw new Error('That sign-in link was already used or expired. Request a fresh one.');
  }
  // A 200 without an access token is the admin MFA-pending shape (the PWA has no
  // second-factor UI). Never setAuth(undefined) and bounce through /map back to
  // /login — surface honest copy instead.
  if (!res.data.access_token) {
    throw new Error('This account uses two-factor sign-in. Please sign in from the admin app.');
  }
  return res.data;
}

export async function requestPasswordReset(email: string): Promise<{ message: string }> {
  const res = await api.post('auth/forgot-password', { json: { email } }).json<ApiResponse<{ message: string }>>();
  return res.data;
}

export async function resetPassword(email: string, token: string, password: string): Promise<{ message: string }> {
  const res = await api.post('auth/reset-password', { json: { email, token, password } }).json<ApiResponse<{ message: string }>>();
  return res.data;
}

export async function fetchMe(): Promise<PublicUser> {
  const res = await api.get('auth/me').json<ApiResponse<PublicUser>>();
  return res.data;
}

/**
 * Boot-time silent refresh (used by useBootstrapAuth before the auth gate
 * decides Login-vs-app). Restores a session from the durable httpOnly
 * refresh cookie — the 30-day half — when localStorage was evicted (iOS
 * Safari ITP / storage pressure) but the cookie is still valid, so the
 * driver isn't bounced to Login despite a live session.
 *
 * Uses raw `ky` (not the `api` instance) so the afterResponse 401
 * interceptor never engages here — the boot refresh and the reactive
 * refresh stay independent paths and can't double-fire. The optional
 * `signal` lets the caller's bootstrap timeout abort a hung request.
 *
 * Returns true iff a session was restored (setAuth ran). Every way it
 * returns false leaves an event (status and server message, never a
 * token) in the auth ring buffer.
 */
export async function bootstrapSilentRefresh(signal?: AbortSignal): Promise<boolean> {
  const outcome = await attemptRefresh(() => postRefresh(signal));
  if (outcome.kind !== 'success') {
    // 401 (no / expired cookie), abort, or network failure → no restore.
    const detail = failureDetail(outcome);
    appendAuthEvent({
      reason: signal?.aborted ? 'boot-timeout' : 'boot-refresh-failed',
      stage: 'refresh',
      ...detail,
    });
    return false;
  }
  if (signal?.aborted) {
    appendAuthEvent({ reason: 'boot-timeout', stage: 'refresh' });
    return false;
  }
  const token = outcome.token;

  // Seed the token so fetchMe() authenticates, then hydrate the user.
  useAuthStore.getState().setAccessToken(token);
  try {
    // One retry: on weak garage signal a good refresh followed by a single
    // failed /me used to drop a valid session. A 401 or 403 is not retried;
    // the 401 hook has already had its say on those.
    const user = await retryOnce(fetchMe, {
      shouldRetry: (err) => {
        const status = httpStatusOf(err);
        return !signal?.aborted && (status === null || !isSessionEndingStatus(status));
      },
    });
    if (signal?.aborted) {
      appendAuthEvent({ reason: 'boot-timeout', stage: 'me' });
      useAuthStore.getState().clearAuth();
      return false;
    }
    useAuthStore.getState().setAuth(user, token);
    return true;
  } catch (err) {
    // Cookie refreshed but /me still failed: don't leave a half-session.
    const detail = failureDetail(await classifyAuthFailure(err));
    logoutWithReason('boot-me-failed', { stage: 'me', ...detail });
    return false;
  }
}

export async function logout(): Promise<void> {
  await api.post('auth/logout').catch(() => null);
  logoutWithReason('manual-logout', { trigger: 'logout-helper' });
}

/**
 * Sign out of every device: the server revokes every refresh token the
 * account holds, in both apps, then this device signs out. auth/me goes
 * first so an expired access token is refreshed (the 401 hook above),
 * because the revoke needs to know whose sessions to end. Resolves false,
 * and this device stays signed in, when the server did not confirm it.
 */
export async function logoutEverywhere(): Promise<boolean> {
  try {
    await api.get('auth/me');
    const res = await api.post('auth/logout', { searchParams: { everywhere: '1' } })
      .json<ApiResponse<{ ok: true; signed_out_everywhere?: boolean }>>();
    if (!res.success || res.data.signed_out_everywhere !== true) return false;
  } catch {
    return false;
  }
  logoutWithReason('manual-logout', { trigger: 'logout-everywhere' });
  return true;
}

// ── AI assistant (driver-facing chat) ───────────────────────────────────────

/**
 * Backend wraps Claude Haiku 4.5 with three sandboxed tools:
 *   - get_charger_status_for_session  (read)
 *   - trigger_soft_reset              (write, audited)
 *   - trigger_hard_reset              (write, audited; requires driver consent)
 *
 * The server enforces session/charger ownership against the JWT
 * subject, so a misbehaving model or prompt-injection attack inside
 * the user's own message cannot cross the user boundary.
 *
 * conversation_id is generated client-side (`crypto.randomUUID()`)
 * and persists for one chat session. The server keeps history for
 * 1h with a 24-message cap.
 */

export interface AssistantToolCall {
  name: string;
  input: Record<string, unknown>;
  result_summary: string;
}

export interface AssistantSuggestedAction {
  contact_support: 'contact_support';
  soft_reset_fired: 'soft_reset_fired';
  hard_reset_fired: 'hard_reset_fired';
}

/** A driver action EVII has PROPOSED -- report_problem, or a Phase 1 own-data
 *  reversible toggle (favorite station, notification preferences, theme) -- that
 *  needs the driver's explicit confirmation before it runs. The client renders a
 *  confirm card; confirming calls confirmAssistantAction with the server-issued
 *  token, which runs the SAME guarded path a human uses, as the session user. */
export interface AssistantPendingAction {
  token: string;
  kind: 'report_problem' | 'favorite' | 'notification_prefs' | 'theme';
  title: string;
  summary: string;
  detail: string;
  /** Verb for the confirm button (e.g. 'Confirm & file', 'Add favorite',
   *  'Switch theme'). The card falls back to 'Confirm' when absent. */
  confirmLabel?: string;
}

export interface AssistantMessageResponse {
  assistant_message: string;
  tool_calls_made: AssistantToolCall[];
  suggested_action:
    | 'contact_support'
    | 'soft_reset_fired'
    | 'hard_reset_fired'
    | null;
  /** Set by prepare_my_receipt: the validated date range the client uses
   *  to fetch the authenticated PDF via downloadSessionsReportPdf. Null on
   *  every other turn. */
  receipt_download?: { from: string; to: string } | null;
  /** Set by suggest_navigation: an allowlisted destination the client renders
   *  as a tappable button. Null on every other turn. */
  navigation?: { route: string; path: string; label: string } | null;
  /** Set by an action tool (report_problem) that PROPOSES a consequential action
   *  needing the driver's explicit confirmation. The client renders a confirm
   *  card; confirming calls confirmAssistantAction. Null on every other turn. */
  pending_action?: AssistantPendingAction | null;
  _meta?: {
    cost_usd: number;
    day_spent_usd: number;
    day_budget_usd: number;
  };
}

export async function sendAssistantMessage(input: {
  message: string;
  conversation_id: string;
  session_id?: string;
  charger_id?: string;
  surface?: string;
  /** Active UI language — EVII replies in it. */
  locale?: SupportedLocale;
}): Promise<AssistantMessageResponse> {
  const res = await api
    .post('ai-assistant/message', { json: input })
    .json<ApiResponse<AssistantMessageResponse>>();
  return res.data;
}

export interface ConfirmActionResult {
  status: 'filed' | 'cancelled';
  ticket_id?: string;
  message: string;
}

/**
 * Confirm (or cancel) a drafted EVII action -- the execute half of the
 * propose -> confirm -> execute pattern. On 'confirm' the server runs the action
 * (e.g. files the report through the same guarded support path a human uses, as
 * the session user); on 'cancel' it discards the draft. Returns a message to show.
 */
export async function confirmAssistantAction(
  conversation_id: string,
  token: string,
  decision: 'confirm' | 'cancel',
): Promise<ConfirmActionResult> {
  const res = await api
    .post('ai-assistant/confirm', { json: { conversation_id, token, decision } })
    .json<ApiResponse<ConfirmActionResult>>();
  return res.data;
}

/**
 * Synthesize EVII's answer text to speech via the driver speak proxy
 * (/ai-assistant/speak) and return the STREAMING Response so the caller can play
 * it progressively (playSpeechStream) -- the voice starts after the first chunk
 * instead of the whole clip. Text-to-audio only: the endpoint holds no data and
 * the AWS credentials stay server-side (task-role IAM). The shared `api` client
 * injects the driver's bearer token. Voice maps server-side (female=Tiffany,
 * male=Matthew generative).
 */
export async function synthesizeDriverSpeech(
  text: string,
  voice: 'female' | 'male' = 'female',
  locale?: SupportedLocale,
): Promise<Response> {
  return api.post('ai-assistant/speak', { json: { text, voice, locale } });
}

// ── Sessions ─────────────────────────────────────────────────────────────────

export async function fetchSessionHold(id: string): Promise<import('@evi/shared').SessionHoldSummary | null> {
  const res = await api.get(`sessions/${encodeURIComponent(id)}/hold`).json<ApiResponse<import('@evi/shared').SessionHoldSummary | null>>();
  return res.data;
}

export interface SessionData {
  idle_view?: import('@evi/shared').SessionIdleView | null;
  idle_report?: import('@evi/shared').IdleReport;
  idle_policy_at_start?: { version: 1; rate_cents: number; grace_minutes: number; billed_cap_minutes: number; max_charge_cents: number | null } | null;
  idle_tracking?: { state: string; charging_ended_at?: string | null; grace_ends_at?: string | null; evidence_until?: string | null;
    cable_removed_at?: string | null; idle_cents: number; billable_minutes: number; finalized_at?: string | null } | null;
  idle_push_delivery?: { complete?: { delivered: number; reason: string }; billing?: { delivered: number; reason: string } };
  _id: string;
  charger_id: string;
  connector_id: number;
  location_id: string;
  /** Owning tenant of the session's charger. Residential home sessions
   *  file under the caller's headless home tenant, so matching this
   *  against GET /me/home's tenant _id identifies "a session at MY home"
   *  client-side (useHomeSession). Rides the raw doc spread on both the
   *  list and detail endpoints; optional for defensive parsing. */
  tenant_id?: string;
  rfid_tag?: string;
  /** Autocharge enrolment offer for THIS live session (server-evaluated once;
   *  never the vehicle id). Only on /sessions/active rows. */
  autocharge_candidate?: { present: boolean; activated?: boolean; pending_on_link?: boolean };
  /** Tap-first fob offer (F3): present while unanswered; window_id once taken up. */
  fob_candidate?: { present: boolean; window_id: string | null; activated?: boolean };
  transaction_id: string;
  status: string;
  /** Stranded-session flag: the charger would not release and the driver
   *  has to act. OPTIONAL because the active-sessions projection may not
   *  carry it — absent must be read as "no problem", never as false-y
   *  trouble. Consumed by the charging-game alert derivation. */
  needs_force_stop?: boolean;
  /** 'captured' / 'capture_failed' / 'overage_failed' / … Same optionality
   *  caveat as `needs_force_stop`. */
  payment_status?: string;
  started_at: string;
  ended_at?: string;
  energy_kwh: number;
  duration_minutes: number;
  meter_start: number;
  meter_stop?: number;
  cost: { energy_cost: number; time_cost: number; flat_fee: number; tax: number; total: number; currency: string };
  /** EVI's connection fee pinned at start (Tier 2), in cents. A NUMBER,
   *  0 included, means the session is pinned and `cost.flat_fee` IS
   *  EVI's connection fee as billed; null or absent means a legacy
   *  session whose `cost.flat_fee` is the tariff's own session fee. See
   *  lib/sessionFee.ts. */
  connection_fee_at_start_cents?: number | null;
  stripe_payment_intent_id?: string;
  /** Stripe-hosted receipt URL — denormalized from Payout in
   *  the payment_intent.succeeded webhook. Powers the View
   *  Invoice button in the session detail sheet. Null on
   *  sessions that never captured a charge (free / wallet-only /
   *  RFID-only) or where the webhook hasn't fired yet (the
   *  field arrives a few seconds late on the absolute-newest
   *  sessions). */
  stripe_receipt_url?: string | null;
  /** The start's pre-auth hold, when one was placed. With the wallet
   *  fields and manual_review_required, what lib/sessionPayment.ts reads
   *  to say "No charge" or "Released" on a start that never happened. */
  pre_auth_amount_cents?: number | null;
  hold_summary?: import('@evi/shared').SessionHoldSummary | null;
  pre_auth_payment_intent_id?: string | null;
  wallet_debited?: boolean;
  /** What the wallet paid, in the session's currency. */
  wallet_debit_cents?: number;
  /** A USD wallet paid this CAD session at the pinned Bank of Canada rate. */
  wallet_fx_debit?: SessionWalletFxDebit | null;
  manual_review_required?: boolean;
  meter_values: Array<{ timestamp: string; energy_kwh: number; power_kw: number; soc_percent?: number; voltage?: number; current?: number }>;
  // Hydrated fields populated by the /sessions/active endpoint so the
  // mini bar + ChargingPage can label the session without an extra
  // round-trip per render.
  location_name?: string;
  charger_identity?: string | null;
  charger_label?: string | null;
  /** Max kW of the connector backing this session, joined from
   *  Charger.connectors[]. Drives the Power Flow bar's normalization
   *  on /charging/active. `null` means the field couldn't be
   *  resolved (legacy data, charger mutation post-session-start) —
   *  the PWA hides the bar entirely rather than picking a fallback
   *  divisor. Populated by /sessions/active; absent on payloads
   *  from list / detail endpoints that don't yet hydrate it. */
  connector_max_power_kw?: number | null;
  /** Customer-facing rate in effect right now, in cents per kWh (or
   *  per hour when `tariff_rate_type` is 'per_hour'): the flat base
   *  rate, or the TOU window active at the time of the read. Display
   *  only — the running `cost` on the row is computed server-side on
   *  every MeterValues from the same tariff. Null when the session
   *  has no tariff or the tariff has no usable rate; the live view
   *  hides the rate cell then. Populated by /sessions/active. */
  tariff_rate_cents?: number | null;
  tariff_rate_type?: 'per_kwh' | 'per_hour';
  /** Lifecycle phase signaled by /sessions/active so the live home,
   *  ChargingPage and MiniChargingBar render the right UX:
   *    'pending'   — Session.status === 'pending', the shell created
   *                  at the Start tap; the charger has not sent
   *                  StartTransaction yet (plug-in window). Nothing
   *                  is flowing; energy/cost are zero.
   *    'active'    — Session.status === 'active', live charging.
   *    'finishing' — Session.status === 'completed' but the
   *                  connector still reports OCPP `Finishing`
   *                  (plug attached, no current draw). The PWA
   *                  hides the kW number, freezes the wavy fill,
   *                  and shows a "Plug still attached" cue.
   *  Absent on payloads from list / detail endpoints that don't
   *  hydrate it. */
  phase?: 'pending' | 'active' | 'finishing';
  /** Phase 1 fleet platform — billing attribution pinned at start.
   *  Always present on new sessions (default 'user'); absent on
   *  legacy rows that pre-date the field. Surface on list views as
   *  a fleet badge when set to 'fleet'. */
  billed_to?: 'user' | 'fleet';
  /** Pinned Fleet doc id. Use SessionDetail.fleet_name for the
   *  human-readable display name (only populated on detail GET). */
  fleet_id?: string | null;
  /** Weekly charging streak stamp (Gamification Ticket 2) — written
   *  by the earning engine at completion and spread through
   *  GET /sessions/:id. `weekly_streak_advanced` is true only when
   *  THIS session moved the streak to a new ISO week (a same-week
   *  repeat stamps false); `weekly_streak_milestone_points` is the
   *  bonus genuinely paid by this session, 0 when none. Absent on
   *  fleet-mode, abuse-gated, and pre-ticket sessions. Drives the
   *  SessionCompletePage streak moment. */
  weekly_streak_advanced?: boolean;
  weekly_streak_weeks?: number;
  weekly_streak_milestone_points?: number;
}

export interface SessionDetail extends SessionData {
  charger_identity: string;
  charger_label: string;
  location_name: string;
  /** Tariff's rate_type at session-time — surfaced by GET /sessions/:id
   *  via a parallel TariffModel projection. Drives the Rate row's unit
   *  on SessionCompletePage. Defaults to 'per_kwh' for legacy sessions
   *  without a tariff or with a pre-Commit-D tariff. */
  tariff_rate_type?: 'per_kwh' | 'per_hour';
  /** Phase 5 multi-window TOU segmentation. Populated on sessions
   *  that ran across a Location with inline TOU windows. Absent /
   *  empty array for flat-rate sessions and sessions that pre-date
   *  Phase 5. SessionCompletePage renders a breakdown table when
   *  length > 1; single-segment sessions keep the existing single-
   *  rate display. Date fields serialize as ISO strings on the
   *  wire — convert with `new Date(s.start_at)` for display. */
  cost_segments?: Array<{
    start_at: string;
    end_at: string;
    duration_minutes: number;
    energy_kwh: number;
    rate_cents: number;
    rate_type: 'per_kwh' | 'per_hour';
    window_label: string;
    cost_cents: number;
  }>;
  /** Phase 3 of gamification rules update — Google Maps place_id
   *  for this session's location, surfaced from the Location doc.
   *  Drives the review CTA on SessionCompletePage. Absent when the
   *  location admin hasn't set one. */
  google_place_id?: string;
  /** Phase 3 of gamification rules update — derived state for the
   *  review CTA, computed server-side at session-fetch time:
   *    'awarded'     — driver already collected the +5 (ledger row
   *                    exists). CTA renders disabled from first paint.
   *    'available'   — location has google_place_id, no ledger row
   *                    yet. CTA enabled with "+5 pts" label.
   *    'unavailable' — location lacks google_place_id. CTA hidden. */
  review_award_state?: 'awarded' | 'available' | 'unavailable';
  /** Tracked review link — the server-minted absolute URL to
   *  GET /r/review/<token>, which logs the click, awards the +5 on
   *  the same idempotency key as the tap-award, and 302s to the
   *  Google review page. Present only when the session is reviewable;
   *  absent on servers that pre-date the feature (the CTA falls back
   *  to the locally-built writereview URL). Token expires 24h after
   *  the session was last fetched. */
  review_redirect_url?: string;
  /** Phase 1 fleet platform — billing attribution pinned at session
   *  start. 'user' = legacy / personal billing (the default for any
   *  session created before fleet platform shipped). 'fleet' =
   *  Fleet's Stripe customer paid. Drives the "Billed to X" pill
   *  on PreSessionPage / SessionDetailPage / SessionCompletePage. */
  billed_to?: 'user' | 'fleet';
  /** Pinned Fleet doc id when billed_to='fleet'; null/undefined
   *  otherwise. The PWA renders against fleet_name (below) for
   *  display; this field exists for diagnostics. */
  fleet_id?: string | null;
  /** Human-readable Fleet name resolved server-side via the
   *  Session→Fleet join. Null when billed_to='user'. */
  fleet_name?: string | null;
}

export interface PaginatedResponse<T> { success: boolean; data: T[]; meta: PaginationMeta }

export async function fetchSessions(params?: { page?: number; limit?: number; status?: string; from?: string; to?: string }): Promise<PaginatedResponse<SessionData>> {
  const searchParams = new URLSearchParams();
  if (params) for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') searchParams.set(k, String(v));
  return api.get('sessions', { searchParams }).json<PaginatedResponse<SessionData>>();
}

/**
 * Driver-facing session list, ALWAYS scoped by user_id on the
 * server. Distinct from `fetchSessions` which hits the role-aware
 * `/sessions` endpoint — that endpoint returns tenant-wide data
 * for non-driver roles, which the driver PWA never wants. Use
 * this helper anywhere the surface is "my sessions" regardless
 * of whether the caller happens to be an admin testing the PWA.
 *
 * Pagination + Load More: caller passes page (1-based) + limit;
 * total session count rides on `meta.total` so the UI knows when
 * to hide the "Load more" CTA.
 */
export async function fetchMySessions(
  params?: { page?: number; limit?: number; status?: string },
): Promise<PaginatedResponse<SessionData>> {
  const searchParams = new URLSearchParams();
  if (params) for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') searchParams.set(k, String(v));
  }
  return api.get('me/sessions', { searchParams }).json<PaginatedResponse<SessionData>>();
}

/**
 * Unified driver activity feed — sessions + wallet top-ups +
 * points ledger + coupon redemptions. Powers the History page's
 * Activity tab.
 *
 * Each row is a discriminated union: `type` tells the renderer
 * which `payload` shape to expect. Phase 2 server-side. Phase 4
 * (this client side) types the payloads concretely so the
 * inline detail rendering can be type-narrowed without runtime
 * shape checks.
 */
export type ActivityType = 'session' | 'wallet_topup' | 'points' | 'coupon';

/** Session payload — full Session doc shape, hydrated server-
 *  side with location_name + charger_identity when those exist
 *  on the underlying record. Mirrors what /me/sessions returns
 *  for a single row, but the payload here is from the raw
 *  $unionWith projection (no batched hydration on the activity
 *  endpoint — kept simple for v1). */
// Activity endpoint doesn't hydrate location_name/charger_identity
// — keeping the projection cheap. The renderer falls back to the
// transaction_id when names aren't present. No additional fields
// over SessionData today, so this is a type alias rather than an
// empty interface (lint: @typescript-eslint/no-empty-object-type).
export type ActivitySessionPayload = SessionData;

/** Wallet top-up payload — projected from an audit_logs row
 *  with action='wallet.topup'. Numeric fields ride on
 *  `response`; the original requested amount lives on `payload`.
 *  Both come from the wallet route's writeAuditLog call so the
 *  shape is stable. */
export interface ActivityWalletTopupPayload {
  _id: string;
  user_id: string;
  tenant_id: string;
  action: 'wallet.topup';
  resource: string;
  payload: {
    requestedCents?: number;
    paymentMethodId?: string;
  };
  response: {
    chargedCents?: number;
    bonusCreditCents?: number;
    tierBonusCents?: number;
    totalCreditCents?: number;
    paymentIntentId?: string;
    newBalanceCents?: number;
    claimId?: string;
    /** The wallet's currency (2026-10-25). Absent on older rows (USD). */
    currency?: string;
  };
  created_at: string;
}

/** Points ledger payload — direct projection of a PointsLedger
 *  row. event_type drives the human-readable copy in the
 *  inline detail renderer; reference_id + reference_type are
 *  used for cross-links (session id → session detail sheet). */
export interface ActivityPointsPayload {
  _id: string;
  user_id: string;
  tenant_id: string;
  event_type: PointsEventType;
  points_delta: number;
  balance_after: number;
  reference_id?: string;
  reference_type?: string;
  note?: string;
  metadata?: Record<string, unknown>;
  created_at: string;
}

/** Coupon claim payload — driver's redemption of a coupon code.
 *  status moves through claimed → applied; applied_to_type tells
 *  whether the redemption discounted a session or a wallet
 *  top-up. */
export interface ActivityCouponPayload {
  _id: string;
  user_id: string;
  tenant_id: string;
  coupon_id: string;
  coupon_code: string;
  status: 'claimed' | 'applied' | 'expired' | 'void';
  applied_to_type?: 'session' | 'wallet_topup';
  applied_to_id?: string;
  discount_applied_cents?: number;
  bonus_points_awarded?: number;
  claimed_at: string;
  applied_at?: string;
}

export type ActivityEntry =
  | { _id: string; type: 'session'; occurred_at: string; payload: ActivitySessionPayload }
  | { _id: string; type: 'wallet_topup'; occurred_at: string; payload: ActivityWalletTopupPayload }
  | { _id: string; type: 'points'; occurred_at: string; payload: ActivityPointsPayload }
  | { _id: string; type: 'coupon'; occurred_at: string; payload: ActivityCouponPayload };

export interface ActivityListResponse {
  success: boolean;
  data: ActivityEntry[];
  meta: PaginationMeta & { types: ActivityType[] };
}

/**
 * Activity tab dashboard summary — companion to fetchChargingSummary.
 * Returns period-scoped aggregations powering the Activity tab's
 * hero card (netPointsChange) + 4-card stat grid (earned,
 * redeemed, wallet top-ups \$, coupons count).
 *
 * Wire-shape mirrors the server contract in
 * apps/api/src/routes/me/activity-summary.ts.
 */
export interface ActivitySummary {
  netPointsChange: number;
  pointsEarned: number;
  pointsRedeemed: number;
  walletTopupCents: number;
  couponsRedeemedCount: number;
}

export async function fetchMyActivitySummary(
  params: { from: string | null; to: string | null },
): Promise<ActivitySummary> {
  const searchParams = new URLSearchParams();
  if (params.from) searchParams.set('from', params.from);
  if (params.to) searchParams.set('to', params.to);
  const res = await api.get('me/activity/summary', { searchParams })
    .json<ApiResponse<ActivitySummary>>();
  return res.data;
}

export async function fetchMyActivity(
  params?: {
    page?: number;
    limit?: number;
    types?: ActivityType[];
    from?: string | null;
    to?: string | null;
    includeSessionPoints?: boolean;
  },
): Promise<ActivityListResponse> {
  const searchParams = new URLSearchParams();
  if (params?.page !== undefined) searchParams.set('page', String(params.page));
  if (params?.limit !== undefined) searchParams.set('limit', String(params.limit));
  if (params?.types && params.types.length > 0) {
    searchParams.set('types', params.types.join(','));
  }
  if (params?.from) searchParams.set('from', params.from);
  if (params?.to) searchParams.set('to', params.to);
  if (params?.includeSessionPoints) searchParams.set('includeSessionPoints', 'true');
  return api.get('me/activity', { searchParams }).json<ActivityListResponse>();
}

export async function fetchActiveSessions(): Promise<SessionData[]> {
  const res = await api.get('sessions/active').json<ApiResponse<SessionData[]>>();
  return res.data;
}

export interface SessionPoints {
  /** Points this session earned for the caller — the sum of its
   *  SESSION_COMPLETED + SESSION_BONUS ledger rows. 0 for a
   *  fleet-sponsored session (fleet pays, no personal points), a
   *  cooldown / threshold-blocked session, or one not yet completed. */
  points_earned: number;
  earned: boolean;
}

/**
 * Per-session points the caller earned. Owner-scoped server-side:
 * GET /me/sessions/:id/points confirms the session is the caller's and
 * sums only their own ledger rows for it. Drives the honest points
 * line on the session detail surfaces.
 */
export async function fetchSessionPoints(id: string): Promise<SessionPoints> {
  const res = await api.get(`me/sessions/${id}/points`).json<ApiResponse<SessionPoints>>();
  return res.data;
}

/**
 * Honest per-session points copy for the detail surfaces. Branches by
 * session type so the line never promises points where none accrue: a
 * not-yet-completed session shows the no-charge note, a fleet-sponsored
 * session says so plainly, and an earning session shows its real value.
 * Shared by the History detail sheet and the routed SessionDetailPage
 * so the two stay in lockstep.
 */
export function sessionPointsNote(
  isCompleted: boolean,
  billedToFleet: boolean,
  loading: boolean,
  data?: SessionPoints,
): string {
  if (!isCompleted) return 'No charge. Session ended before energy delivery.';
  if (billedToFleet) return 'Fleet sponsored. No personal points on this session.';
  if (loading) return 'Calculating points…';
  const earned = data?.points_earned ?? 0;
  if (earned > 0) {
    return `Earned ${earned} ${earned === 1 ? 'point' : 'points'}, now in your EVI wallet.`;
  }
  return 'No points earned for this session.';
}

export async function fetchSession(id: string): Promise<SessionDetail> {
  const res = await api.get(`sessions/${id}`).json<ApiResponse<SessionDetail>>();
  return res.data;
}

export interface AwardReviewPointsResult {
  awarded: boolean;
  already_awarded: boolean;
  points: number;
}

/**
 * Award the trust-on-tap +5 for the session-complete Google review
 * CTA (Phase 3 of gamification rules update).
 *
 * Server-side idempotent — calling twice for the same session_id
 * returns `{ awarded: false, already_awarded: true }` on the second
 * call without writing a second ledger row. The CTA flow uses this
 * for both the initial tap (likely `awarded: true`) and as the
 * authoritative answer when the page renders for a session whose
 * award already landed.
 */
export async function awardReviewPoints(sessionId: string): Promise<AwardReviewPointsResult> {
  const res = await api
    .post(`me/sessions/${sessionId}/award-review-points`)
    .json<ApiResponse<AwardReviewPointsResult>>();
  return res.data;
}

/**
 * Location-scoped review-points award. Distinct from the session-
 * scoped helper above: same +5 / REVIEW_AWARDED event_type, but
 * gated by a 60-day per-location cooldown via fixed-window
 * idempotency keys. Returns `already_awarded: true` with
 * `next_eligible_at` when the caller is mid-cooldown — the CTA
 * uses this to flip into its disabled state without re-fetching
 * the eligibility endpoint.
 */
export interface AwardLocationReviewPointsResult {
  awarded: boolean;
  already_awarded: boolean;
  points: number;
  /** ISO timestamp of when the driver becomes eligible again at
   *  this location. Present on `already_awarded: true` responses;
   *  absent on the fresh-award path. */
  next_eligible_at?: string;
}

export async function awardLocationReviewPoints(
  locationId: string,
): Promise<AwardLocationReviewPointsResult> {
  const res = await api
    .post(`me/locations/${locationId}/award-review-points`)
    .json<ApiResponse<AwardLocationReviewPointsResult>>();
  return res.data;
}

/**
 * Location review eligibility check. Fired on StationDetailPage
 * mount so the Leave-a-review CTA renders in its correct initial
 * state (enabled / cooldown / hidden) without a tap-and-discover
 * round-trip. Carries `google_place_id` on the actionable
 * branches so the PWA has everything it needs to construct the
 * Google Maps deep link without a separate fetch.
 */
export type LocationReviewEligibility =
  | {
      eligible: true;
      reason: 'eligible';
      google_place_id: string;
      /** Tracked review link — server-minted absolute URL to
       *  GET /r/review/<token> (location-only token, 24h expiry).
       *  The CTA opens it instead of the locally-built Google URL so
       *  the click logs server-side; the award shares the 60-day
       *  window key, so nothing double-pays. Optional: absent on
       *  servers that pre-date the feature (the CTA falls back to
       *  the local URL). */
      review_redirect_url?: string;
      last_awarded_at: null;
      next_eligible_at: null;
    }
  | {
      eligible: false;
      reason: 'cooldown_active';
      google_place_id: string;
      review_redirect_url?: string;
      last_awarded_at: string;
      next_eligible_at: string;
    }
  | {
      eligible: false;
      reason: 'not_reviewable';
    };

export async function fetchLocationReviewEligibility(
  locationId: string,
): Promise<LocationReviewEligibility> {
  const res = await api
    .get(`me/locations/${locationId}/review-eligibility`)
    .json<ApiResponse<LocationReviewEligibility>>();
  return res.data;
}

// ── Gamification ─────────────────────────────────────────────────────────────

export interface GamificationProfile {
  user_id: string;
  username?: string;
  avatar_style?: AvatarStyle;
  avatar_seed?: string;
  avatar_url?: string;
  equipped_skin?: string;
  points: number;
  total_kwh: number;
  total_sessions: number;
  current_rank?: string;
  next_rank?: string;
  points_to_next_rank?: number;
  badges_earned: number;
  leaderboard_position?: number;
  /**
   * Theme codes the driver has most-recently-applied, newest first,
   * capped at 4. Mix of registry family IDs ('default', 'luce') and
   * skin unlock codes ('skin_midnight', ...). Empty for new users
   * who haven't applied anything yet — the PWA picker falls back to
   * the first-4-from-registry default until this populates.
   */
  recent_themes: string[];
}

export interface BadgeInfo {
  _id: string;
  badge_id: string;
  name: string;
  description: string;
  icon_url?: string;
  trigger_type: string;
  threshold: number;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  is_active: boolean;
  earned: boolean;
  earned_at?: string;
}

export interface RankInfo {
  _id: string;
  name: string;
  tier: 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond';
  min_points: number;
  min_kwh: number;
  min_sessions: number;
  perks: string[];
  skin_unlocks: string[];
  display_order: number;
}

export interface LeaderboardEntry {
  user_id: string;
  username?: string;
  avatar_style?: AvatarStyle;
  avatar_seed?: string;
  avatar_url?: string;
  current_rank?: string;
  points: number;
  total_kwh: number;
  total_sessions: number;
  position: number;
}

export async function fetchProfile(): Promise<GamificationProfile> {
  const res = await api.get('gamification/profile').json<ApiResponse<GamificationProfile>>();
  return res.data;
}

/**
 * Single-shot gamification summary for the RewardsPage hero + badge
 * grid + history timeline. Replaces the old fan-out of fetchProfile +
 * client-side badge filtering + mocked history rows.
 */
export async function fetchUserSummary(): Promise<UserGamificationSummary> {
  const res = await api
    .get('gamification/user-summary')
    .json<ApiResponse<UserGamificationSummary>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

export async function fetchBadges(): Promise<BadgeInfo[]> {
  const res = await api.get('gamification/badges').json<ApiResponse<BadgeInfo[]>>();
  return res.data;
}

export async function fetchRanks(): Promise<RankInfo[]> {
  const res = await api.get('gamification/ranks').json<ApiResponse<RankInfo[]>>();
  return res.data;
}

export async function fetchLeaderboard(scope: 'global' | 'tenant' = 'global'): Promise<LeaderboardEntry[]> {
  const res = await api.get('gamification/leaderboard', { searchParams: { scope, limit: '100' } }).json<ApiResponse<LeaderboardEntry[]>>();
  return res.data;
}

export async function setUsername(username: string): Promise<{ username: string }> {
  const res = await api.post('gamification/username', { json: { username } }).json<ApiResponse<{ username: string }>>();
  return res.data;
}

export async function setAvatar(style: AvatarStyle, seed: string): Promise<{ avatar_style: AvatarStyle; avatar_seed: string; avatar_url: string }> {
  const res = await api.post('gamification/avatar', { json: { style, seed } }).json<ApiResponse<{ avatar_style: AvatarStyle; avatar_seed: string; avatar_url: string }>>();
  return res.data;
}

export async function equipSkin(skinId: string): Promise<{ equipped_skin: string }> {
  const res = await api.post('gamification/skin', { json: { skin_id: skinId } }).json<ApiResponse<{ equipped_skin: string }>>();
  return res.data;
}

// ── Chargers / Locations ─────────────────────────────────────────────────────

export interface PublicCharger {
  _id: string;
  identity: string;
  vendor: string;
  model: string;
  ocpp_version: string;
  location_id: string;
  is_online: boolean;
  is_active: boolean;
  connectors: Array<{ connector_id: number; type: string; max_power_kw: number; status: string }>;
  maintenance_windows?: Array<{ _id?: string; start: string; end: string; reason: string }>;
}

export interface PublicLocation {
  _id: string;
  name: string;
  type: string;
  address: { street: string; city: string; state: string; zip: string; country: string; coordinates?: { lat: number; lng: number } };
  /** Visible-charger count from THIS user's perspective. For tenant
   *  members at their own locations: count of active chargers. For
   *  cross-tenant guest views: count of chargers with public_network
   *  AND is_open_to_guests AND is_active. Always > 0 when the
   *  location appears in the driver_map response (empty pins are
   *  dropped server-side per the public-network filter). */
  charger_count: number;
  /** Online-charger count from THIS user's perspective. Server-
   *  derived in the driver_map list response (B-3 fix); the popup
   *  and bottom sheet read this directly instead of computing from
   *  a separate tenant-scoped fetch. Optional for back-compat. */
  online_chargers?: number;
  is_active: boolean;
  is_visible_on_driver_map?: boolean;
  /** True when at least one charger at this location is guest-
   *  eligible (public_network=true AND is_open_to_guests=true).
   *  Helps the PWA render different chrome for tenant-only vs
   *  mixed-access locations. Optional for backward compat — older
   *  serializations round-trip as undefined. */
  has_guest_eligible?: boolean;
  /** Embedded per-stall list filtered to chargers visible to THIS
   *  user. Eliminates the separate tenant-scoped fetchPublicChargers
   *  call from the map. Each row carries access_mode and
   *  guest_markup_bps so dot tone + per-stall guest-pricing badges
   *  can render off this data alone. */
  chargers?: PublicLocationDetailCharger[];
  /** Operator-supplied hero photo URL. Null/empty/absent → fallback
   *  to the gradient placeholder for now; Street View tier lands with
   *  the queued StationHero ticket. */
  photo_url?: string | null;
  /** Optional eyebrow above the station title (Novelle treatment). */
  subtitle?: string | null;
}

/**
 * Driver map locations.
 *
 * Passes `driver_map=1` so the server applies the visibility contract
 * (active + visible_on_driver_map + coords present) itself, instead of
 * the client filtering down a superset. This means the PWA map can't
 * silently drop a location on a client-side predicate mismatch — if
 * the driver sees "0 nearby," the server logs the exact filter
 * dimension that ate the docs for diagnostic triage.
 */
/**
 * Driver-map locations fetch.
 *
 * `currentMode` (Fleet Mode map-visibility extension) shapes the
 * cross-tenant visibility branch on the server: 'public' widens the
 * server filter to surface all `public_network: true` chargers,
 * 'fleet' (or undefined) keeps the narrow `is_open_to_guests: true`
 * predicate. Chargers visible only via the widened branch carry the
 * new `access_mode: 'guest_closed'` which the PWA's accessibility
 * classifier reads as 'inaccessible'.
 *
 * Callers in non-map contexts (ScanPage, etc.) leave `currentMode`
 * undefined to inherit the safe historical narrow behaviour.
 */
export async function fetchPublicLocations(
  currentMode?: 'fleet' | 'public',
): Promise<PublicLocation[]> {
  const searchParams: Record<string, string> = {
    driver_map: '1',
    limit: '500',
  };
  if (currentMode) {
    searchParams['current_mode'] = currentMode;
  }
  const res = await api
    .get('locations', { searchParams })
    .json<PaginatedResponse<PublicLocation>>();
  return res.data;
}

export async function fetchPublicChargers(locationId?: string): Promise<PublicCharger[]> {
  const sp = new URLSearchParams({ limit: '500' });
  if (locationId) sp.set('location_id', locationId);
  const res = await api.get('chargers', { searchParams: sp }).json<PaginatedResponse<PublicCharger>>();
  return res.data;
}

/**
 * Single-charger fetch. Accepts either the OCPP identity string (from
 * the QR payload or any permalink) or the Mongo ObjectId.
 *
 * Hits the dedicated driver endpoint `GET /chargers/driver/:idOrIdentity`
 * (cross-tenant via resolveDriverCharger; tight projection that omits
 * admin-only fields). The admin endpoint `GET /chargers/:id` is
 * tenant-scoped and returns the full doc — never call it from the
 * PWA: a guest at a cross-tenant charger would 404 there. Switching
 * the PWA to this endpoint also future-proofs the response: any
 * admin-only field added to Charger later won't leak to drivers.
 *
 * `access_mode` and `guest_markup_bps` are added by commit 4 of the
 * third-party-charger-access ticket. They're optional in the type
 * because the field arrived in v2.x — clients pinned to older
 * builds (none exist today, but defensively typed for rollback).
 */
export interface PublicChargerDetail extends PublicCharger {
  commissioning_test_allowed?: boolean;
  preparing_required_connectors?: number[];
  tariff_id?: string | null;
  last_heartbeat?: string;
  last_boot?: string;
  /** Per-charger access mode resolved against THIS user:
   *    'tenant'  — own-tenant charger; no markup applies.
   *    'guest'   — cross-tenant via public_network + is_open_to_guests.
   *                guest_markup_bps may apply.
   *    'granted' — cross-tenant where this user is on the operator's
   *                guest_pass_list. Semi-private — invisible to other
   *                drivers, but this user has explicit access.
   *                guest_markup_bps may still apply (operators can
   *                grant access AND charge a markup).
   *    'guest_closed' — Fleet Mode map-visibility extension. Cross-
   *                tenant charger surfaced only because the caller
   *                requested `current_mode=public` (which widens the
   *                server filter beyond is_open_to_guests=true). The
   *                operator has NOT opted in to guest charging at this
   *                charger; the driver can see it on the map but
   *                start-session will reject. PWA accessibility
   *                classifier reads this as 'inaccessible' so the
   *                marker renders muted + grayscale. */
  access_mode?: 'tenant' | 'guest' | 'granted' | 'guest_closed';
  guest_markup_bps?: number | null;
  /** Fleet attribution + server-computed deriveChargerAccessMode
   *  classification — surfaced so PreSessionPage can run the
   *  client-side accessibility predicate BEFORE the driver taps
   *  Start. Mirror of the embedded-charger fields on the driver-
   *  map response (Phase 9). */
  fleet_id?: string | null;
  fleet_access_mode?: 'public' | 'fleet_only' | 'mixed';
  /** The private switch (Warren, 2026-10-22): a private charger this driver
   *  may not start. The pre-start screen shows "This is a private charger"
   *  instead of a Start button. Absent from an older Api. */
  private_locked?: boolean;
}
export async function fetchPublicCharger(idOrIdentity: string): Promise<PublicChargerDetail> {
  const res = await api
    .get(`chargers/driver/${idOrIdentity}`)
    .json<ApiResponse<PublicChargerDetail>>();
  return res.data;
}

// ── My Chargers (resident-managed) ──────────────────────────────────

export interface MyCharger {
  _id: string;
  identity: string;
  /** Owner-set label, or null for the identity fallback. */
  display_name?: string | null;
  vendor: string;
  model: string;
  ocpp_version: string;
  location_id: string;
  location_name: string;
  connectors: Array<{
    connector_id: number;
    type: string;
    max_power_kw: number;
    /** Operator-set rated current ceiling (amps); absent on most home chargers. */
    max_current_a?: number;
    status: string;
  }>;
  is_online: boolean;
  is_active: boolean;
  is_open_to_guests: boolean;
  public_network: boolean;
  photo_url: string | null;
  created_via: 'admin' | 'resident_self_service';
  created_at: string;
  /** Register-first (2026-10-02): how to connect the charger, built server
   *  side (never the password). Null for chargers not registered by a
   *  resident; absent from an API older than register-first. */
  connection?: MyChargerConnection | null;
}

export type MyChargerConnectionState =
  | 'awaiting_first_connection'
  | 'provisioning'
  | 'needs_credentials'
  | 'connected';

export interface MyChargerConnection {
  /** OCPP_PUBLIC_WS_URL + '/' + identity: what the owner types into the app. */
  url: string;
  identity: string;
  subprotocol: 'ocpp1.6';
  /** 'password': reveal it on demand. 'first_connection': leave it blank,
   *  EVI sends it on first connect. 'none': no password on file. */
  password_mode: 'password' | 'first_connection' | 'none';
  state: MyChargerConnectionState;
  /** Set in first_connection mode, and in needs_credentials (password
   *  mode) after a setup window closed with no key sent while a re-arm is
   *  left: the owner may type the password or open setup again. */
  first_connection: {
    window_ends_at: string | null;
    window_open: boolean;
    can_rearm: boolean;
  } | null;
}

export interface RevealedChargerPassword {
  /** Null when there is nothing to type (first_connection or none). */
  password: string | null;
  password_mode: MyChargerConnection['password_mode'];
  /** How long the page may show it before clearing it. */
  display_seconds: number;
}

/** The owner's OCPP password for their own resident-registered charger.
 *  Audited server side before it is returned; 6 a minute. */
export async function revealMyChargerPassword(id: string): Promise<RevealedChargerPassword> {
  const res = await api
    .post(`my-chargers/${id}/credentials/reveal`)
    .json<ApiResponse<RevealedChargerPassword>>();
  return res.data;
}

/** Open a fresh first-connection window (home chargers whose model uses
 *  first-connection setup, while no key has been sent). */
export async function armMyChargerFirstConnection(id: string): Promise<MyChargerConnection | null> {
  const res = await api
    .post(`my-chargers/${id}/first-connection`)
    .json<ApiResponse<{ connection: MyChargerConnection | null }>>();
  return res.data.connection;
}

export interface ResidentRegisterModelChoice {
  model: string;
  /** False for a model that cannot connect to the EV Initiative network. */
  network_compatible: boolean;
}

/** The optional model picker on the register form. The form adds its own
 *  "Other / not sure" choice. */
export async function fetchResidentRegisterModels(): Promise<ResidentRegisterModelChoice[]> {
  const res = await api
    .get('chargers/resident-register/models')
    .json<ApiResponse<{ models: ResidentRegisterModelChoice[] }>>();
  return res.data.models;
}

/** List the chargers OWNED by the calling user (filter on
 *  Charger.created_by_user_id). Backs the PWA "My Chargers" list. */
export async function fetchMyChargers(): Promise<MyCharger[]> {
  const res = await api.get('my-chargers').json<ApiResponse<MyCharger[]>>();
  return res.data;
}

// ── My Charger detail (Phase 3.1) ───────────────────────────────────

/** Full driver-tier projection — list shape + the editable settings
 *  block (guest pass list, time windows, markup, toggles) and the
 *  contact-notes Map. Returned by GET /my-chargers/:id and the
 *  PATCH response so a save can re-render off the response without
 *  a follow-up GET. */
/** Location-electricity payload bundled into the detail/PATCH
 *  responses. Carries the building's rate config + a resolved-now
 *  snapshot the resident-side guest-pricing UI needs to render the
 *  dual $/% input + math explanation.
 *
 *  `resolved_rate.source` discriminator drives the UI's four
 *  variants:
 *    'flat'       → flat $X.XX/kWh — happy path
 *    'inline_tou' → range display ($X – $Y/kWh) + average $ math
 *    'tariff'     → same as inline_tou; the difference is admin-side
 *                   (saved tariff vs ad-hoc window list)
 *    'none'       → no rate configured; block the input
 *
 *  `tou_min_cents` / `tou_max_cents` are populated only for the two
 *  TOU sources; flat/none leave them null. */
export interface MyChargerLocationPayload {
  name: string;
  /** Flat rate field. Null when the location uses TOU / tariff
   *  exclusively, OR when the location has no electricity config. */
  electricity_rate_cents: number | null;
  /** Inline TOU windows (Location.electricity_tou_windows). Null
   *  when empty or absent. Each entry exposes its own rate_cents +
   *  rate_type. rate_type optional on the wire — defaults to per_kwh
   *  server-side for legacy windows that pre-date Commit B. */
  electricity_tou_windows: Array<{
    day_of_week: number[];
    start_hour: number;
    end_hour: number;
    rate_cents: number;
    rate_type?: 'per_kwh' | 'per_hour';
  }> | null;
  /** Tariff snapshot when the location references a saved Tariff
   *  doc. Carries only what the rate-range UI needs (type + rates). */
  electricity_tariff: {
    type: string;
    rates: Record<string, unknown>;
  } | null;
  /** Resolved-now snapshot — the rate a session started right now
   *  would lock in. Drives the dual $/% input's math. rate_type
   *  was added in Commit B's resolver (and propagated through the
   *  Tariff branch in 03f9057) but never surfaced on this FE type
   *  until B.1.1 — defaults to per_kwh for back-compat with payloads
   *  served before the resolver change. */
  resolved_rate: {
    rate_cents: number | null;
    source: 'tariff' | 'inline_tou' | 'flat' | 'none';
    rate_type: 'per_kwh' | 'per_hour';
    tou_min_cents: number | null;
    tou_max_cents: number | null;
  };
}

export interface MyChargerDetail extends MyCharger {
  guest_markup_bps: number | null;
  /** Where this charger's guest price is set (item 6): 'home_guest_rate'
   *  at a home, 'building_unavailable' for a resident's charger anywhere
   *  else (no paid guests at building plugs yet), 'markup' for a charger
   *  the resident did not register. Absent from an older Api. */
  guest_pricing?: 'home_guest_rate' | 'building_unavailable' | 'markup';
  /** Open to guests with no guest price, so every paying guest is turned
   *  away (item 6). Absent from an older Api. */
  guest_price_missing?: boolean;
  guest_pass_list: string[];
  /** Identities for guest_pass_list, so chips render a person instead
   *  of an ObjectId. Absent entries mean the account was deleted. */
  guest_pass_list_members?: PassListUserRow[];
  /** Blocked drivers — deny beats everything including the pass list.
   *  A denied guest sees the charger as simply closed. */
  guest_deny_list: string[];
  guest_deny_list_members?: PassListUserRow[];
  /** IANA zone guest windows are enforced in. NULL = the location has
   *  no timezone and the gate falls back to UTC — surfaces must say
   *  so, never claim "your local time". */
  window_timezone?: string | null;
  /** True only at a home, where the owner may set the zone themselves. */
  window_timezone_can_set?: boolean;
  guest_time_windows: Array<{
    start_hour: number;
    end_hour: number;
    days_of_week: number[];
  }>;
  contact_notes: Record<string, string>;
  /** Owner-chosen runtime amperage cap (amps), or null for no cap. Display only;
   *  it is not the rated ceiling and never a billing field. */
  owner_amperage_cap_a: number | null;
  /** Null only when the Location ref is missing (defensive — should
   *  not happen in practice; every charger has a location). */
  location: MyChargerLocationPayload | null;
  /** Slice D — residential monetization state. Null for chargers at
   *  non-residential locations, so multifamily surfaces are untouched.
   *  utility_rate is the owner's Home Settings electricity estimate,
   *  surfaced here for the margin line beside the guest price; min/max
   *  give the honest range for TOU/tiered rates. */
  home_monetization: {
    enrolled: boolean;
    guest_rate_cents: number | null;
    /** EVI's connection fee on this charger, in cents: read-only here.
     *  Set by EVI, kept by EVI, charged once per paid session on top of
     *  the guest price and never out of the host's 85%. Null = no fee
     *  at this charger. (The host-set plug-in fee, `flat_fee_cents`, is
     *  retired: the host can no longer set any fee.) */
    connection_fee_cents: number | null;
    connection_fee_owner?: 'evi';
    /** Idle fee, cents per idle minute. Null = not set. */
    time_rate_cents: number | null;
    /** The idle fee's grace, minutes (5-30); null without a fee. */
    idle_grace_minutes?: number | null;
    idle_max_charge_cents?: number | null;
    utility_rate: {
      type: 'flat' | 'time_of_use' | 'tiered';
      base_cents: number | null;
      min_cents: number | null;
      max_cents: number | null;
    } | null;
    /** The home's currency, 'USD' or 'CAD' (item 3): the one a save sets
     *  every price in (the input prefix and the margin line) and the
     *  owner's electricity rate. Null when the address settles no
     *  country; absent from an older Api (read as USD). */
    currency?: string | null;
    /** The currency guests pay the stored guest_rate_cents,
     *  time_rate_cents and connection fee in today: the live Home
     *  Guest's. Null with no stored price. Differs from `currency` only at
     *  a home priced before item 3, until its next save moves it. Absent
     *  from an older Api. */
    guest_rate_currency?: string | null;
  } | null;
}

/**
 * Wrap an earning-enable call so a 409 PAYOUT_TERMS_REQUIRED raises the
 * payout-terms accept card and, if the user accepts, retries the call once.
 * Any other error (and a decline) propagates as-is. Used by the earning
 * surfaces whose server route gates on payout_terms.
 */
async function withPayoutTermsRetry<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    const resp = (err as { response?: Response }).response;
    if (resp?.status !== 409) throw err;
    const body = (await resp.clone().json().catch(() => null)) as ApiError | null;
    if (body?.error?.code !== 'PAYOUT_TERMS_REQUIRED') throw err;
    const accepted = await requestTermsAcceptance('payout_terms');
    if (!accepted) throw err;
    return await fn();
  }
}

/** Slice D — enroll the caller's home in monetization. Server verifies
 *  the disclaimer ack + Express readiness; the call is the opt-in. Gated on
 *  payout_terms — a first-time earner is prompted to accept then retried. */
export async function enrollHomeMonetization(): Promise<void> {
  await withPayoutTermsRetry(() =>
    api.post('me/home/monetization/enroll').json<ApiResponse<{ enrolled: boolean }>>());
}

/** The two owner-settable legs of the Home Guest tariff, plus EVI's
 *  connection fee for the charger, read-only. `null` on any leg means
 *  "not set" — the editor renders an empty field. */
export interface HomeGuestPricing {
  guest_rate_cents: number | null;
  time_rate_cents: number | null;
  /** The idle fee's grace, minutes (5-30); null without a fee. */
  idle_grace_minutes?: number | null;
  idle_max_charge_cents?: number | null;
  /** EVI's fee on the charger (Tier 2). Never host-settable. */
  connection_fee_cents: number | null;
  /** The home's currency (item 3), the one a save sets; null with no
   *  country, absent from an older Api. */
  currency?: string | null;
  /** The currency guests pay the stored price in today (the live Home
   *  Guest's); null with no stored price, absent from an older Api. */
  guest_rate_currency?: string | null;
}

export async function fetchHomeGuestPricing(): Promise<HomeGuestPricing> {
  const res = await api.get('me/home/guest-rate').json<ApiResponse<HomeGuestPricing>>();
  return res.data;
}

/** Slice D — set the home guest price (cents per kWh, 1..200), plus the
 *  optional idle fee.
 *
 *  The idle fee is sent as `null` to CLEAR it and omitted entirely to
 *  leave it alone. The caller passes `undefined` for "don't touch".
 *  There is no fee key for the connection fee: it is EVI's, and the
 *  route no longer accepts a host-set `flat_fee_cents`. */
export async function updateHomeGuestRate(
  guestRateCents: number,
  fees?: { timeRateCents?: number | null; idleGraceMinutes?: number | null; idleMaxChargeCents?: number | null },
): Promise<void> {
  await api
    .patch('me/home/guest-rate', {
      json: {
        guest_rate_cents: guestRateCents,
        ...(fees && fees.idleMaxChargeCents !== undefined ? { idle_max_charge_cents: fees.idleMaxChargeCents } : {}),
        ...(fees && fees.timeRateCents !== undefined
          ? { time_rate_cents: fees.timeRateCents }
          : {}),
        ...(fees && fees.idleGraceMinutes !== undefined
          ? { idle_grace_minutes: fees.idleGraceMinutes }
          : {}),
      },
    })
    .json<ApiResponse<HomeGuestPricing>>();
}

export async function fetchMyCharger(id: string): Promise<MyChargerDetail> {
  const res = await api
    .get(`my-chargers/${id}`)
    .json<ApiResponse<MyChargerDetail>>();
  return res.data;
}

/** Partial-update body for PATCH /my-chargers/:id. Each field is
 *  independently optional — the server only writes keys that are
 *  present, so a single-toggle save doesn't blow away the rest of
 *  the doc. */
export interface UpdateMyChargerBody {
  /** Owner-editable charger label; null clears back to the identity fallback. */
  display_name?: string | null;
  is_open_to_guests?: boolean;
  guest_markup_bps?: number | null;
  public_network?: boolean;
  guest_pass_list?: string[];
  /** Deny beats everything, including the pass list. */
  guest_deny_list?: string[];
  guest_time_windows?: Array<{
    start_hour: number;
    end_hour: number;
    days_of_week: number[];
  }>;
}

export async function updateMyCharger(
  id: string,
  body: UpdateMyChargerBody,
): Promise<MyChargerDetail> {
  const res = await api
    .patch(`my-chargers/${id}`, { json: body })
    .json<ApiResponse<MyChargerDetail>>();
  return res.data;
}

/** Set a residential home charger's runtime amperage cap (amps), or pass null to
 *  clear it back to full. Owner-gated and residential-scoped server-side; rides
 *  the SetChargingProfile relay. Returns the stored cap. Throws on offline (409). */
export async function setChargerAmperage(
  id: string,
  amps: number | null,
): Promise<{ owner_amperage_cap_a: number | null }> {
  const res = await api
    .post(`chargers/${id}/amperage`, { json: { amps } })
    .json<ApiResponse<{ owner_amperage_cap_a: number | null }>>();
  return res.data;
}

/** User-search row returned by the resident-tier
 *  /my-chargers/users/search typeahead. Backs the "Guest pass
 *  list" picker on the My Chargers detail page.
 *
 *  Two source paths server-side:
 *    - Same-tenant substring (the common partial-name case).
 *    - Cross-tenant exact-match when the query is a full email or
 *      E.164 phone — used to add visiting friends from other
 *      buildings. The flag `is_cross_tenant=true` powers the
 *      PWA's "Outside building" indicator and skips the
 *      same-tenant ergonomic where applicable. */
export interface PassListUserRow {
  _id: string;
  email: string;
  username: string | null;
  name: string | null;
  is_cross_tenant: boolean;
}

/** Outcome of an exact-match pass-list lookup. `incomplete` means the
 *  term was not a full email or full E.164 phone, so the server did not
 *  look anything up; `ambiguous` means a phone matched more than one
 *  account (phone is not unique) and the host must use an email. */
export interface PassListLookupResult {
  user: PassListUserRow | null;
  incomplete: boolean;
  ambiguous: boolean;
}

/**
 * Resolve ONE user by a COMPLETE email address or E.164 phone number.
 *
 * Replaces the former substring typeahead (removed 2026-08-05): partial
 * terms returned other drivers' emails and real names, which let anyone
 * with a charger enumerate the tenant directory. There is no partial
 * matching left anywhere in this flow — the server refuses to look up
 * anything short of a full identifier.
 */
export async function lookupUserForPassList(
  query: string,
  options?: { chargerId?: string },
): Promise<PassListLookupResult> {
  const term = query.trim();
  if (!term) return { user: null, incomplete: true, ambiguous: false };
  const sp = new URLSearchParams({ q: term });
  if (options?.chargerId) sp.set('charger_id', options.chargerId);
  const res = await api
    .get('my-chargers/users/search', { searchParams: sp })
    .json<ApiResponse<PassListUserRow[]> & { incomplete?: boolean; ambiguous?: boolean }>();
  return {
    user: res.data[0] ?? null,
    incomplete: res.incomplete === true,
    ambiguous: res.ambiguous === true,
  };
}

// ── My Charger sessions + earnings (Phase 3.2) ────────────────────

/** Driver-tier session row returned by GET /my-chargers/:id/sessions.
 *  No payout internals, no Stripe ids — strictly the resident-facing
 *  view of "who used my charger." */
export interface MyChargerSession {
  idle_fee_cents?: number | null;
  _id: string;
  user_id: string | null;
  /** Hydrated from User.username || User.name. Null when the
   *  session row had no user_id (anonymous / system-stopped). */
  user_name: string | null;
  start_time: string;
  stop_time: string | null;
  kwh_consumed: number;
  total_cost_cents: number;
  /** The currency total_cost_cents is in (item 3). Absent: USD. */
  currency?: string;
  access_mode: 'tenant' | 'guest';
  was_guest: boolean;
}

export async function fetchMyChargerSessions(id: string): Promise<MyChargerSession[]> {
  const res = await api
    .get(`my-chargers/${id}/sessions`)
    .json<ApiResponse<MyChargerSession[]>>();
  return res.data;
}

/** Earnings summary returned by GET /my-chargers/:id/earnings. All
 *  amounts are in cents; the PWA formats for display. last_payout is
 *  null when no payout has cleared yet on this charger. */
export interface MyChargerEarnings {
  current_currency?: string | null;
  all_time_cents: number;
  this_month_cents: number;
  last_30_days_cents: number;
  last_payout: { amount_cents: number; date: string; currency?: string; cash_by_currency?: import('@evi/shared').CashAmount[] | null } | null;
  /** The currency the fields above are in (item 3): one only, never a
   *  blend. Absent from an older Api (USD). */
  currency?: string;
  /** Each currency's figures, only when this charger has earned in more
   *  than one (USD history beside CAD). One line each, never summed. */
  by_currency?: Array<{
    currency: string;
    all_time_cents: number;
    this_month_cents: number;
    last_30_days_cents: number;
    last_payout: { amount_cents: number; date: string; currency?: string; cash_by_currency?: import('@evi/shared').CashAmount[] | null } | null;
  }>;
}

export async function fetchMyChargerEarnings(id: string): Promise<MyChargerEarnings> {
  const res = await api
    .get(`my-chargers/${id}/earnings`)
    .json<ApiResponse<MyChargerEarnings>>();
  return res.data;
}

/**
 * Host-level carbon-credit summary (carbon epic, display commit).
 * HOST-scoped, not per-charger: credits accrue to the charger owner
 * across all their chargers, and the three states are kept distinct —
 * accruing is an ESTIMATE (annual claim cycle), realized_paid is
 * confirmed money, realized_unpaid is earned money awaiting payout
 * setup. Amounts are grouped per currency (jurisdiction-derived).
 */
export interface MyCarbonCurrencyBucket {
  currency: string;
  accruing_estimate_cents: number;
  accruing_energy_kwh: number;
  realized_paid_cents: number;
  realized_unpaid_cents: number;
}
export interface MyCarbonSummary {
  currencies: MyCarbonCurrencyBucket[];
  historical: { rows: number; energy_kwh: number; share_cents: number } | null;
  needs_payout_setup: boolean;
  program_year: number;
}

export async function fetchMyCarbonSummary(): Promise<MyCarbonSummary> {
  const res = await api.get('me/carbon-summary').json<ApiResponse<MyCarbonSummary>>();
  return res.data;
}

// ── My Charger contacts (Phase 4.1) ───────────────────────────────

/** One row per pass-list user on a resident-owned charger. Phone is
 *  optional (E.164 when present). Note is null when the resident
 *  hasn't written one. added_at is best-effort from the audit log
 *  and may be null on legacy adds before the audit pipe was wired. */
export interface MyChargerContact {
  user_id: string;
  /** owner = the caller's own usage row; pass_list = current free-
   *  pass-list member (full contact surface); paying_guest = charged
   *  here without being pass-listed; visitor = opened the share link
   *  / QR but has no billed sessions yet. paying_guest and visitor
   *  are identity-only — email/phone/note always null. */
  role: 'owner' | 'pass_list' | 'paying_guest' | 'visitor';
  name: string | null;
  email: string | null;
  phone: string | null;
  note: string | null;
  added_at: string | null;
  /** Usage at this charger (completed sessions). Zeros for
   *  pass-listed people who never charged. */
  sessions: number;
  kwh: number;
  last_charged_at: string | null;
  paid_cents: number;
  /** The currency paid_cents is in (item 3). Absent: USD. */
  currency?: string;
  /** Each currency they paid in, only when there are two. */
  paid_by_currency?: Array<{ currency: string; cents: number }>;
  /** When they last opened the share link / QR; null when never. */
  last_seen_at: string | null;
}

export async function fetchMyChargerContacts(id: string): Promise<MyChargerContact[]> {
  const res = await api
    .get(`my-chargers/${id}/contacts`)
    .json<ApiResponse<MyChargerContact[]>>();
  return res.data;
}

/** One person's charging history at one charger, for the owner's
 *  contact detail page. */
export interface ContactSessionRow {
  session_id: string;
  started_at: string | null;
  ended_at: string | null;
  kwh: number;
  /** The currency every amount on this row is in (item 3). Absent: USD. */
  currency?: string;
  /** What the driver was billed for this session. */
  driver_paid_cents: number;
  /** EVI's connection fee inside driver_paid_cents (Tier 2): EVI's, never
   *  part of the host's earnings. Optional: an Api built before Tier 2
   *  does not send it. */
  connection_fee_cents?: number;
  /** What the OWNER earned. Null while the payout hasn't cleared —
   *  render "pending", never $0. */
  earned_cents: number | null;
  payout_status: string | null;
}

export interface ContactSessionHistory {
  contact: {
    user_id: string;
    role: 'owner' | 'pass_list' | 'paying_guest';
    name: string | null;
    /** Null for anyone not on the pass list (two-tier PII). */
    email: string | null;
    phone: string | null;
    note: string | null;
  };
  totals: {
    sessions: number;
    kwh: number;
    driver_paid_cents: number;
    connection_fee_cents?: number;
    earned_cents: number;
    pending_sessions: number;
    /** The currency the money totals are in (item 3); absent: USD. */
    currency?: string;
    /** Each currency's totals, only when the sessions are in two. */
    by_currency?: Array<{
      currency: string;
      driver_paid_cents: number;
      connection_fee_cents: number;
      earned_cents: number;
    }>;
  };
  sessions: ContactSessionRow[];
}

/** Set the charger LOCATION's timezone (residential only; a shared
 *  building 403s with SHARED_LOCATION_TIMEZONE). */
export async function setMyChargerTimezone(
  chargerId: string,
  timezone: string,
): Promise<{ timezone: string }> {
  const res = await api
    .put(`my-chargers/${chargerId}/timezone`, { json: { timezone } })
    .json<ApiResponse<{ timezone: string }>>();
  return res.data;
}

export async function fetchContactSessionHistory(
  chargerId: string,
  contactUserId: string,
): Promise<ContactSessionHistory> {
  const res = await api
    .get(`my-chargers/${chargerId}/contacts/${contactUserId}/sessions`)
    .json<ApiResponse<ContactSessionHistory>>();
  return res.data;
}

/** Set or clear the per-contact note. Pass null or "" to clear.
 *  Returns the resulting { user_id, note } pair so the caller can
 *  patch local state without a follow-up GET. */
export async function updateContactNote(
  chargerId: string,
  userId: string,
  note: string | null,
): Promise<{ user_id: string; note: string | null }> {
  const res = await api
    .patch(`my-chargers/${chargerId}/contacts/${userId}`, { json: { note } })
    .json<ApiResponse<{ user_id: string; note: string | null }>>();
  return res.data;
}

/** Remove a contact from the pass list (and drop their note).
 *  Returns the updated charger detail so the caller can refresh
 *  the pass-list shape locally. */
export async function removeContact(
  chargerId: string,
  userId: string,
): Promise<MyChargerDetail> {
  const res = await api
    .delete(`my-chargers/${chargerId}/contacts/${userId}`)
    .json<ApiResponse<MyChargerDetail>>();
  return res.data;
}

// ── My Pass-List Access (Phase 4.2) ───────────────────────────────

/** A charger the caller has been given pass-list access to,
 *  cross-tenant. Returned by GET /my-pass-list-access. Owner info is
 *  included so the guest can reach out via the contact-card actions
 *  on the page. guest_time_windows + guest_markup_bps drive the
 *  "what can I do here?" copy. */
export interface PassListAccessRow {
  charger_id: string;
  identity: string;
  /** Hardware description, fed to chargerMedia so the guest's card
   *  shows the same model loop the owner sees on My Chargers. */
  vendor: string | null;
  model: string | null;
  location_id: string;
  location_name: string;
  photo_url: string | null;
  is_online: boolean;
  is_open_to_guests: boolean;
  guest_markup_bps: number | null;
  guest_time_windows: Array<{
    start_hour: number;
    end_hour: number;
    days_of_week: number[];
  }>;
  /** Zone those window hours are enforced in. Null = UTC fallback;
   *  the card labels it as UTC rather than implying a local clock. */
  window_timezone?: string | null;
  owner: {
    user_id: string;
    name: string | null;
    /** null for source==='visited' — the owner never chose to share
     *  contact details with a history-only guest. */
    email: string | null;
    phone: string | null;
  } | null;
  /** granted = current pass-list member; visited = the caller has
   *  billed sessions here (history source — no consent implied). */
  source: 'granted' | 'visited';
  last_charged_at: string | null;
  added_at: string | null;
}

export async function fetchMyPassListAccess(): Promise<PassListAccessRow[]> {
  const res = await api
    .get('my-pass-list-access')
    .json<ApiResponse<PassListAccessRow[]>>();
  return res.data;
}

// ── RFID self-registration (tap capture) ────────────────────────────
// Driver opens a short window bound to one charger, taps their fob,
// the rejected Authorize is matched back, they confirm and pay.

export interface MyRfidCard {
  _id: string;
  tag_uid: string;
  label: string | null;
  is_active: boolean;
  /** Paid for and waiting for its second sighting (tap it, then start
   *  charging from the app) - shown as Pending, not Inactive. */
  pending?: boolean;
  created_at: string;
}

export interface RfidCaptureWindow {
  _id: string;
  status: 'open' | 'matched' | 'confirmed' | 'cancelled' | 'expired' | 'void_ambiguous';
  charger_identity: string;
  /** Fee pinned when the window opened — what was disclosed is what is charged. */
  amount_cents: number;
  /** Masked (last four) - a fob UID is a bearer credential. */
  matched_tag: string | null;
  /** 'app_first': opened here, then tapped. 'session_offer': tapped before
   *  an app start, offered on the charging screen. */
  matched_via?: 'app_first' | 'session_offer' | null;
  tapped_at?: string | null;
  /** This fob will bind PENDING (no grant at this charger - tap-first offer
   *  or app first alike): shown before payment. */
  pending_on_bind?: boolean;
  /** Poll only: the tap was the driver's own pending fob; the window closed
   *  (status 'cancelled') with this hint instead of running out. */
  own_pending?: boolean;
  expires_at: string;
}

export async function fetchMyRfidCards(): Promise<MyRfidCard[]> {
  const res = await api.get('me/rfid/cards').json<ApiResponse<MyRfidCard[]>>();
  return res.data;
}

/** Open a capture window at the charger the driver is standing at: an id or
 *  the OCPP identity from a scan or typed from the label. The server
 *  requires the charger live and showing Preparing (F3 presence). */
export async function openRfidCaptureWindow(charger: string): Promise<RfidCaptureWindow> {
  const res = await api
    .post('me/rfid/capture-windows', { json: { charger } })
    .json<ApiResponse<RfidCaptureWindow>>();
  return res.data;
}

/** Tap-first offer on the charging screen: the server re-derives the tag and
 *  returns an already-matched window, confirmed through the ordinary paid
 *  confirm (confirmRfidCaptureWindow). */
export async function acceptFobSessionOffer(sessionId: string): Promise<RfidCaptureWindow> {
  const res = await api
    .post(`me/rfid/session-offers/${sessionId}/accept`, { json: {} })
    .json<ApiResponse<RfidCaptureWindow>>();
  return res.data;
}

export async function dismissFobSessionOffer(sessionId: string): Promise<void> {
  await api.post(`me/rfid/session-offers/${sessionId}/dismiss`, { json: {} });
}

export async function pollRfidCaptureWindow(id: string): Promise<RfidCaptureWindow> {
  const res = await api
    .get(`me/rfid/capture-windows/${id}`)
    .json<ApiResponse<RfidCaptureWindow>>();
  return res.data;
}

export async function confirmRfidCaptureWindow(
  id: string,
  label?: string,
): Promise<{ card_id: string; tag_uid: string; pending?: boolean }> {
  const res = await api
    .post(`me/rfid/capture-windows/${id}/confirm`, { json: label ? { label } : {} })
    .json<ApiResponse<{ card_id: string; tag_uid: string; pending?: boolean }>>();
  return res.data;
}

export async function cancelRfidCaptureWindow(id: string): Promise<void> {
  await api.delete(`me/rfid/capture-windows/${id}`);
}

// ── Resident-tier account fields (Phase 5.1) ──────────────────────

/** Resident-tier User fields not exposed by the baseline /auth/me
 *  projection. Drives the disclaimer-modal + phone-capture gates
 *  on the PWA. Both ack timestamps are ISO strings or null when
 *  unset. */
export interface AccountView {
  phone: string | null;
  first_resident_disclaimer_acked_at: string | null;
  first_pass_list_recipient_disclaimer_acked_at: string | null;
  /** The caller's current tenant. The join modal reads
   *  current_tenant.is_default to decide first-join vs switch
   *  framing. Null only on the defensive case where tenant_id
   *  doesn't resolve. */
  current_tenant: {
    _id: string;
    name: string;
    slug: string;
    is_default: boolean;
  } | null;
  /** Privacy: is this user's username matchable in the public
   *  friend search? Default false (opt-in). Drives the
   *  Privacy toggle in PWA Settings; gates the username match
   *  in /friends/search. */
  discoverable_by_username: boolean;
}

export async function fetchAccount(): Promise<AccountView> {
  const res = await api.get('me/account').json<ApiResponse<AccountView>>();
  return res.data;
}

/**
 * Update privacy preferences. Currently accepts
 * `discoverable_by_username` only; the body shape will grow
 * as future privacy toggles land (Stage 1.5 phone discovery,
 * etc.). Returns the full AccountView so the React Query
 * cache stays consistent.
 */
export async function updatePrivacy(
  patch: { discoverable_by_username?: boolean },
): Promise<AccountView> {
  const res = await api
    .patch('me/privacy', { json: patch })
    .json<ApiResponse<AccountView>>();
  return res.data;
}

// ── Phase 5a Item 11 — notification preferences ───────────────────

export interface PreferencesView {
  resident_alert_opt_in: boolean;
  notify_session_complete: boolean;
  notify_payment_failed: boolean;
  notify_badge_earned: boolean;
  notify_weekly_report: boolean;
  /** Legacy single marketing toggle: on while any category is on and the
   *  driver has not unsubscribed. Older builds read and write it. */
  marketing_opt_in: boolean;
  /** Marketing email by category (23 October 2026): drivers are enrolled by
   *  default with every category on, and can turn each off or unsubscribe
   *  from all. Absent on an older Api. */
  marketing?: MarketingPreferences;
  /** ── Push categories: one field per row in Settings ──────────────
   *  Parallel to the notify_* keys above, which gate EMAIL — a driver
   *  can have push without email or the reverse. All of these are
   *  inert until they grant OS-level permission and a subscription
   *  exists. There is no `account` field: that tier has no off switch.
   *  A registered notification type names the category that mutes it,
   *  so new types land in these rows rather than adding new ones. */
  push_charging: 'all' | 'finish_only' | 'off';
  /** Pleasant categories, so they default OFF. */
  push_social: boolean;
  push_rewards: boolean;
  /** CONSENT, not a preference: product news and discount offers.
   *  Default false, read server-side as a bare `=== true`, and NOT the
   *  same flag as marketing_opt_in — that one's own copy names email,
   *  and consent is sought separately per channel. */
  push_news_offers: boolean;
  /** Charger faults, escalations and approvals. */
  push_alerts: boolean;
  /** How a resident host hears about guest sessions: per session (with a
   *  busy-day threshold that summarises the rest of that day), one daily
   *  summary, or nothing. Only the host changes it; the system never does. */
  push_host_mode: 'each' | 'daily' | 'off';
  /** The driver's EVII voice (female = Tiffany, male = Matthew). Server-side
   *  so the choice follows the driver across devices; the evieVoice store
   *  hydrates from + persists to this. Default 'female'. */
  evie_voice: 'female' | 'male';
  /** The driver's UI language (i18n epic). Server-side so it follows the
   *  driver across devices; the locale store hydrates from + persists to this.
   *  Default 'en'. Legal copy stays English regardless. */
  locale: SupportedLocale;
  /** Derived: true when this user owns ≥1 resident-managed charger
   *  (Charger.created_by_user_id === this user AND
   *  created_via='resident_self_service'). Drives the toggle's
   *  visibility — non-residents don't see it. */
  has_resident_chargers: boolean;
}

export async function fetchPreferences(): Promise<PreferencesView> {
  const res = await api.get('me/preferences').json<ApiResponse<PreferencesView>>();
  return res.data;
}

/** The three marketing categories and the unsubscribe-from-all state. */
export interface MarketingPreferences {
  categories: Record<MarketingCategory, boolean>;
  unsubscribed: boolean;
}

export async function updatePreferences(
  patch: Partial<Omit<PreferencesView, 'has_resident_chargers' | 'marketing'>> & {
    /** Home-screen shortcuts: exactly three distinct ShortcutIds, or null
     *  to return to the persona defaults. Read back on /auth/me, not here. */
    pinned_shortcuts?: readonly string[] | null;
    /** Turn marketing categories on or off. */
    marketing_categories?: Partial<Record<MarketingCategory, boolean>>;
    /** Unsubscribe from all marketing email. */
    marketing_unsubscribe_all?: true;
  },
): Promise<PreferencesView> {
  const res = await api
    .patch('me/preferences', { json: patch })
    .json<ApiResponse<PreferencesView>>();
  return res.data;
}

// ── Email preferences from an emailed link (no sign-in) ──────────────────────

/** What the public /email-preferences page shows: a driver's categories, or
 *  a lead's single subscription. email_hint is masked ("w***@example.com"). */
export type EmailPreferencesState =
  | ({ kind: 'driver'; email_hint: string } & MarketingPreferences)
  | { kind: 'lead'; email_hint: string; unsubscribed: boolean };

/** Read the preferences the signed link names. Throws on an invalid link. */
export async function lookupEmailPreferences(token: string): Promise<EmailPreferencesState> {
  const res = await api
    .post('public/email-preferences/lookup', { json: { token } })
    .json<ApiResponse<EmailPreferencesState>>();
  return res.data;
}

/** "Email me a new link": the Api answers the same for any address and, for a
 *  known one, emails a fresh signed preferences link to it (at most one an
 *  hour). Behind Turnstile, fail-closed: 403 TURNSTILE_FAILED without a live token. */
export async function requestEmailPreferencesLink(email: string, turnstileToken: string): Promise<void> {
  await api
    .post('public/email-preferences/request-link', { json: { email, turnstile_token: turnstileToken } })
    .json<ApiResponse<{ requested: true }>>();
}

/** Change categories, or unsubscribe from all, through the signed link. */
export async function updateEmailPreferences(
  token: string,
  change: { categories?: Partial<Record<MarketingCategory, boolean>>; unsubscribe_all?: true },
): Promise<EmailPreferencesState> {
  const res = await api
    .post('public/email-preferences/update', { json: { token, ...change } })
    .json<ApiResponse<EmailPreferencesState>>();
  return res.data;
}

// ── Birthday reward ───────────────────────────────────────────────────────────

export interface BirthdayView {
  /** month + day only (no year); null until set. */
  birthday: { month: number; day: number } | null;
  /** True once set — the Settings row shows it read-only after this. */
  locked: boolean;
  claimed_this_year: boolean;
  /** birthday set AND reached this year AND not yet claimed this year. */
  claim_eligible: boolean;
  bonus_points: number;
  /** Celebration replay window (7 days from the claim, up to 3 replays). */
  celebration: {
    window_open: boolean;
    replays_used: number;
    replays_remaining: number;
  };
}

export interface BirthdayReplayResult {
  /** False when the cap (3) is hit or the 7-day window has closed. */
  granted: boolean;
  replays_used: number;
  /** True when this grant consumed the final replay ("one last time"). */
  is_last: boolean;
}

/** Count a birthday-celebration replay (server-capped at 3 within 7 days). */
export async function replayBirthdayCelebration(): Promise<BirthdayReplayResult> {
  const res = await api.post('me/birthday/celebration/replay').json<ApiResponse<BirthdayReplayResult>>();
  return res.data;
}

export interface BirthdayClaimResult {
  claimed: boolean;
  already_claimed: boolean;
  /** True when the bonus is withheld by the anti-abuse proof-of-life gate —
   *  a friendly "keep using EV Initiative to unlock" state, NOT an error. */
  withheld?: boolean;
  points_awarded: number;
  new_total?: number | null;
}

export async function fetchBirthday(): Promise<BirthdayView> {
  const res = await api.get('me/birthday').json<ApiResponse<BirthdayView>>();
  return res.data;
}

/** Set the birthday ONCE. Throws (HTTPError 409) if it's already set. */
export async function setBirthday(month: number, day: number): Promise<BirthdayView> {
  const res = await api
    .patch('me/birthday', { json: { month, day } })
    .json<ApiResponse<BirthdayView>>();
  return res.data;
}

/** Claim the birthday bonus. Idempotent server-side (200 with claimed:false if
 *  already claimed this year). */
export async function claimBirthday(): Promise<BirthdayClaimResult> {
  const res = await api.post('me/birthday/claim').json<ApiResponse<BirthdayClaimResult>>();
  return res.data;
}

// ── Terms versioning ──────────────────────────────────────────────────────────

export type TermsDocType = 'driver_tos' | 'payout_terms' | 'sp_terms';

export interface TermsStatus {
  doc_type: TermsDocType;
  label: string;
  version: string;
  /** Inline body; null when the doc is hosted externally (hostedUrl set). */
  body: string | null;
  hosted_url: string | null;
  /** Whether the caller has accepted the CURRENT version. */
  accepted: boolean;
  /**
   * The server has published history for this doc type but no current
   * version, so it cannot say what the driver would be agreeing to. The gate
   * BLOCKS on this rather than treating the doc as accepted.
   */
  unavailable?: boolean;
}

/** Current version + acceptance status for the requested doc types
 *  (default driver_tos). */
export async function fetchTermsStatus(docs?: TermsDocType[]): Promise<TermsStatus[]> {
  const q = docs && docs.length ? `?docs=${docs.join(',')}` : '';
  const res = await api.get(`me/terms/status${q}`).json<ApiResponse<TermsStatus[]>>();
  return res.data;
}

/** Record acceptance of the current version of a doc type. */
export async function acceptTerms(
  docType: TermsDocType,
): Promise<{ doc_type: TermsDocType; version: string; accepted: boolean }> {
  const res = await api
    .post('me/terms/accept', { json: { doc_type: docType } })
    .json<ApiResponse<{ doc_type: TermsDocType; version: string; accepted: boolean }>>();
  return res.data;
}

/** Owner-side disclaimer ack. Idempotent — a second call returns
 *  the existing acked state without rolling forward the timestamp. */
export async function ackResidentDisclaimer(): Promise<AccountView> {
  const res = await api
    .patch('me/disclaimer/resident')
    .json<ApiResponse<AccountView>>();
  return res.data;
}

/** Recipient-side disclaimer ack. Same idempotent semantics. */
export async function ackRecipientDisclaimer(): Promise<AccountView> {
  const res = await api
    .patch('me/disclaimer/recipient')
    .json<ApiResponse<AccountView>>();
  return res.data;
}

/** Update the caller's phone number (E.164). The server enforces the
 *  same regex User.phone uses; a malformed phone returns a 400. */
export async function updateMyPhone(phone: string): Promise<AccountView> {
  const res = await api
    .patch('me/phone', { json: { phone } })
    .json<ApiResponse<AccountView>>();
  return res.data;
}

// ── Driver Themes ticket — theme selection helper ───────────────────

/**
 * Theme family codes mirrored from the @evi/skins ThemeFamilyId
 * union and the User schema enum at packages/db/src/models/User.ts.
 *
 * Inlined here rather than imported from @evi/skins so apps/web's
 * api layer stays free of the registry dependency (the existing
 * dep boundary is intentional — only the theme-rendering surfaces
 * reach into @evi/skins). Three-way lockstep: changes to the union
 * here must also land in:
 *   1. packages/skins/src/contract.ts (ThemeFamilyId)
 *   2. packages/db/src/models/User.ts (active_theme_family enum)
 *   3. packages/shared/src/types/user.ts (PublicUserSchema enum)
 *   4. apps/api/src/routes/me/account.ts (PATCH /me/theme body schema)
 */
export type ThemeFamilyId =
  | 'default'
  | 'luce'
  | 'cartographic'
  | 'neon'
  | 'electric-light'
  | 'dark-electric'
  | 'obsidian';

export type ThemeMode = 'light' | 'dark';

/**
 * Driver Themes ticket — update the caller's active theme family
 * and/or mode. Both fields optional; an empty patch is a 200 no-op
 * returning current values. When `active_theme_family` changes the
 * server also clears `active_skin_code` to null and write-throughs
 * to `recent_themes` atomically (see apps/api/src/routes/me/
 * account.ts PATCH /me/theme handler for the coupled-write
 * details).
 *
 * Returns the narrow `{ active_theme_family, theme_mode }` shape
 * the server projects. Callers that want the auth store synced
 * after a successful save should follow up with
 * `useAuthStore.getState().updateUser({ active_theme_family,
 * theme_mode })` so the persisted PublicUser reflects the new
 * values without firing a /auth/me refetch.
 */
export async function updateMyTheme(patch: {
  active_theme_family?: ThemeFamilyId;
  theme_mode?: ThemeMode;
}): Promise<{ active_theme_family: ThemeFamilyId; theme_mode: ThemeMode }> {
  const res = await api
    .patch('me/theme', { json: patch })
    .json<ApiResponse<{ active_theme_family: ThemeFamilyId; theme_mode: ThemeMode }>>();
  return res.data;
}

/** POST /me/tour-complete — sets the idempotent server flag that the driver
 *  finished or dismissed the EVII guided tour. No body; reaching the endpoint
 *  is the signal. Returns the effective completion timestamp. Callers sync the
 *  auth store (updateUser) so the first-launch auto-open never re-fires. */
export async function completeTour(): Promise<{ tour_completed_at: string | null }> {
  const res = await api
    .post('me/tour-complete')
    .json<ApiResponse<{ tour_completed_at: string | null }>>();
  return res.data;
}

// ── Web push (device registration) ─────────────────────────────────
//
// A subscription is a CAPABILITY: anyone holding the endpoint can push
// to that device. So the endpoint only ever travels between this client
// and our own API, and every route below is scoped server-side to the
// calling driver — none of them accepts a user id.

export interface PushSubscribeBody {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  user_agent?: string | null;
}

/** POST /me/push/subscribe — register or refresh THIS device. Upserts on
 *  the endpoint, so re-subscribing the same browser is idempotent and
 *  never disturbs the driver's other devices. */
export async function subscribePush(body: PushSubscribeBody): Promise<{ subscribed: boolean }> {
  const res = await api
    .post('me/push/subscribe', { json: body })
    .json<ApiResponse<{ subscribed: boolean }>>();
  return res.data;
}

/** DELETE /me/push/subscribe — drop THIS device. ky needs the body on a
 *  DELETE passed explicitly; the server scopes the delete to the caller
 *  so holding someone else's endpoint cannot unsubscribe them. */
export async function unsubscribePush(endpoint: string): Promise<{ subscribed: boolean }> {
  const res = await api
    .delete('me/push/subscribe', { json: { endpoint } })
    .json<ApiResponse<{ subscribed: boolean }>>();
  return res.data;
}

/** POST /me/push/dismiss — "we asked, they said no". Set-once and
 *  server-side, so a decline on one device is not re-asked on another. */
export async function dismissPushPrompt(): Promise<{ dismissed: boolean }> {
  const res = await api
    .post('me/push/dismiss')
    .json<ApiResponse<{ dismissed: boolean }>>();
  return res.data;
}

// ── Announcements (landing popup) ──────────────────────────────────

/** GET /me/announcements — ALL live announcements for this driver's
 *  audience that they haven't permanently dismissed, highest-priority
 *  first. The overlay shows the top one; the notification bell lists them
 *  all. The server owns audience + schedule + geo + priority resolution. */
export async function fetchActiveAnnouncements(): Promise<AnnouncementPublic[]> {
  const res = await api
    .get('me/announcements')
    .json<ApiResponse<{ announcements?: AnnouncementPublic[]; announcement?: AnnouncementPublic | null }>>();
  // `announcements` is the list; fall back to the legacy single-row shape
  // so a client that outruns the API deploy still shows the top popup.
  if (Array.isArray(res.data.announcements)) return res.data.announcements;
  return res.data.announcement ? [res.data.announcement] : [];
}

/** POST /me/announcements/:id/dismiss — permanent + idempotent; the
 *  announcement never returns on any device. */
export async function dismissAnnouncement(id: string): Promise<void> {
  await api.post(`me/announcements/${id}/dismiss`);
}

// ── Stripe Connect Express (Phase 6.1) ────────────────────────────

/** POST /me/connect/start response — { url } the PWA navigates to.
 *  Single-use Stripe-hosted onboarding link. */
/**
 * Mint (or, within 60 s, re-use) the resident's Stripe onboarding link.
 * `legalName` — "Name as on your ID" — prefills the Express account's
 * individual name at CREATION only (an existing account keeps its name).
 * `next` — an allowlisted PWA path (the earn walk) Stripe's return lands
 * on via /my-account/connect/complete?next=…; anything else is ignored
 * server-side.
 * `country` is the bank account country the host chose. Required by the
 * server to CREATE an account (400 CONNECT_COUNTRY_REQUIRED without it,
 * never a default) and ignored for an existing one. Sent only when given.
 */
export async function startConnectOnboarding(
  opts?: { legalName?: string; next?: string; country?: PayoutCountry },
): Promise<{ url: string }> {
  const json = {
    ...(opts?.legalName ? { legal_name: opts.legalName } : {}),
    ...(opts?.next ? { next: opts.next } : {}),
    ...(opts?.country ? { country: opts.country } : {}),
  };
  const res = await withPayoutTermsRetry(() =>
    api.post('me/connect/start', { json }).json<ApiResponse<{ url: string }>>());
  return res.data;
}

/**
 * Start or resume payouts from an entry point that cannot ask for the bank
 * account country (the charger page gate, the Earnings Stripe row, the
 * carbon card, the dashboard 412 fallback). Only a status that says an
 * account exists resumes it at Stripe. No account, a status that could not
 * be read, or a server answer of CONNECT_COUNTRY_REQUIRED (the status was
 * stale) means the caller sends the host to the chooser, payoutChooserPath.
 * Any other error propagates.
 */
export async function startPayoutsOrChoose(
  status: ConnectStatus | null | undefined,
): Promise<{ kind: 'chooser' } | { kind: 'url'; url: string }> {
  if (payoutEntry(status) === 'chooser') return { kind: 'chooser' };
  try {
    const { url } = await startConnectOnboarding();
    return { kind: 'url', url };
  } catch (err) {
    if (await isCountryRequiredError(err)) return { kind: 'chooser' };
    throw err;
  }
}

/**
 * startConnectOnboarding for the two screens that may CREATE the account
 * (the earn walk's Get paid step and the chooser). When a country was
 * sent, the status is read back before the host goes to Stripe: during a
 * rolling deploy an Api task from before 2026-10-02 can still answer the
 * start, ignore the country and create a Canadian account. Then the
 * account's country comes back as `otherCountry`, and the caller shows it
 * instead of sending the host through Stripe's checks on an account
 * Stripe cannot move. A status that cannot be read, or carries no
 * country, lets the link through: there is nothing to check against.
 */
export async function startNewPayoutAccount(
  opts: { legalName?: string; next?: string; country?: PayoutCountry },
): Promise<{ kind: 'url'; url: string } | { kind: 'otherCountry'; country: string }> {
  const { url } = await startConnectOnboarding(opts);
  if (!opts.country) return { kind: 'url', url };
  const status = await fetchConnectStatus().catch(() => null);
  const other = createdInOtherCountry(opts.country, status);
  return other ? { kind: 'otherCountry', country: other } : { kind: 'url', url };
}

/** Real-time view of the resident's Connect Express account.
 *  has_account=false means onboarding has never been started.
 *  charges_enabled + payouts_enabled both true is the sharing
 *  prereq. requirements_currently_due is a list of Stripe field
 *  names the resident still needs to provide. */
export interface ConnectStatus {
  has_account: boolean;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
  requirements_currently_due: string[];
  requirements_disabled_reason: string | null;
  /** Stripe has the fields and is still verifying them — "confirming",
   *  not "needed". Non-empty in the gap between the resident returning
   *  and account.updated landing. */
  requirements_pending_verification: string[];
  /** Named verification failures (e.g. verification_document_name_mismatch)
   *  with Stripe's own reason text. */
  requirements_errors: Array<{ code: string; reason: string; requirement: string }>;
  /** The account's country ('US', 'CA'), null without an account. Shown
   *  read-only: Stripe cannot change it. Optional because an Api served
   *  before 2026-10-02 does not send it. */
  country?: string | null;
  /** The country of the caller's own home, from its address; null when
   *  there is no home or the address does not settle it. A difference
   *  from `country` is a warning only. Optional for the same reason. */
  home_country?: PayoutCountry | null;
}

export async function fetchConnectStatus(): Promise<ConnectStatus> {
  const res = await api
    .get('me/connect/status')
    .json<ApiResponse<ConnectStatus>>();
  return res.data;
}

/**
 * Mint a single-use Stripe Express dashboard URL. The PWA opens
 * this in a new tab from the MyChargerEarningsPage "Open Stripe
 * dashboard" button. LoginLinks are one-shot on Stripe's side, so
 * the caller mints a fresh URL per click rather than caching.
 *
 * Server returns 412 `EXPRESS_NOT_READY` when the resident hasn't
 * completed Connect onboarding. The PWA pre-gates on
 * `fetchConnectStatus().charges_enabled` to avoid the 412 in
 * normal flow; the 412-recovery branch routes to onboarding via
 * `startConnectOnboarding` instead.
 */
export async function fetchDashboardLink(): Promise<{ url: string }> {
  const res = await api
    .post('me/connect/dashboard-link')
    .json<ApiResponse<{ url: string }>>();
  return res.data;
}

export interface LocationTypeaheadRow {
  _id: string;
  name: string;
  address: { street: string; city: string; state: string };
}

/** Typeahead source for the "My Chargers > Add" location picker.
 *  Returns up to 10 active locations matching the query (own tenant
 *  OR any location with public_network+open chargers). */
export async function searchLocationsForTypeahead(query: string): Promise<LocationTypeaheadRow[]> {
  if (!query.trim()) return [];
  const sp = new URLSearchParams({ q: query });
  const res = await api
    .get('locations/typeahead', { searchParams: sp })
    .json<ApiResponse<LocationTypeaheadRow[]>>();
  return res.data;
}

export interface RegisterResidentChargerBody {
  identity: string;
  location_id: string;
  connector_type: 'J1772' | 'NACS' | 'CCS1';
  max_power_kw: number;
  photo_url?: string;
  vendor?: string;
  model?: string;
}

/** Result of GET /chargers/lookup-by-identity. Powers the
 *  AddChargerPage's auto-fill UX — when a driver types a known
 *  identity, vendor/model/connector specs pre-fill from either the
 *  existing DB doc or the live OCPP connection's BootInfo. */
export type ChargerLookupResult =
  | { found: false }
  | {
      found: true;
      source: 'live_connection' | 'db' | 'both';
      vendor: string | null;
      model: string | null;
      ocpp_version: string | null;
      connectors: Array<{
        connector_id: number;
        type: string;
        max_power_kw: number;
      }> | null;
    };

export async function lookupChargerByIdentity(identity: string): Promise<ChargerLookupResult> {
  const trimmed = identity.trim();
  if (!trimmed) return { found: false };
  const sp = new URLSearchParams({ identity: trimmed });
  const res = await api
    .get('chargers/lookup-by-identity', { searchParams: sp })
    .json<ApiResponse<ChargerLookupResult>>();
  return res.data;
}

export async function registerResidentCharger(body: RegisterResidentChargerBody): Promise<MyCharger> {
  // ky throws on non-2xx; surface the structured error code +
  // message so the form can map LOCATION_DISABLED_RESIDENT_REGISTRATION
  // / CHARGER_ALREADY_REGISTERED (409, with error.support_url) into
  // user-friendly copy. Register-first: the charger need not be
  // online, so there is no not-connected answer any more. A 200 is the
  // same owner's retry and carries the record they already have.
  const res = await api
    .post('chargers/resident-register', { json: body })
    .json<ApiResponse<MyCharger>>();
  return res.data;
}

/**
 * Single-location fetch for the pre-session screen hero +
 * StationDetailPage.
 *
 * Hits the dedicated driver endpoint `GET /locations/driver/:id`
 * (cross-tenant visibility via the same public_network +
 * is_open_to_guests gate as the driver_map list). The admin
 * endpoint `GET /locations/:id` is tenant-scoped and would 404
 * for a guest viewing a cross-tenant station — never call it
 * from the PWA.
 *
 * Counts (charger_count, online_chargers) are from THIS user's
 * perspective: tenant members see all active/online chargers at
 * their site; cross-tenant guests see only public_network +
 * is_open_to_guests chargers.
 */
export interface PublicLocationDetailCharger extends PublicCharger {
  /** The operator's name for the stall ("Visitor Spot"). Null when unset,
   *  absent from an older Api; the stall list then shows the identity. */
  display_name?: string | null;
  /** Per-charger access mode resolved against THIS user:
   *    'tenant'  — own-tenant charger.
   *    'guest'   — cross-tenant via public_network + is_open_to_guests
   *                (visible on map for any driver; markup applies).
   *    'granted' — cross-tenant via guest_pass_list (semi-private;
   *                invisible to other drivers, but this user has
   *                explicit operator-grant access). The PWA may
   *                still show a markup chip when guest_markup_bps
   *                is set, but the map tone treats granted rows
   *                identically to own-tenant rows — visibility itself
   *                is the access signal.
   *    'guest_closed' — Fleet Mode map-visibility extension. Surfaced
   *                only when the caller passed `current_mode=public`
   *                to the driver_map endpoint and the charger has
   *                public_network=true but is_open_to_guests=false.
   *                See `mapChargerAccessibility.ts` — the classifier
   *                short-circuits to 'inaccessible' on this value. */
  access_mode?: 'tenant' | 'guest' | 'granted' | 'guest_closed';
  /** Markup in basis points when access_mode is cross-tenant
   *  ('guest' or 'granted'). Null on own-tenant rows or rows whose
   *  markup hasn't been set. */
  guest_markup_bps?: number | null;
  /** Item 6: this driver would be turned away here as a paying guest,
   *  because it is a resident's charger with no guest price. Absent from
   *  an older Api and from the map list. */
  guest_price_missing?: boolean;
  /** Phase 9 — fleet attribution. Null on unattributed (public)
   *  chargers; non-null on chargers assigned to a fleet via
   *  Phase 4's admin UI. Client compares against user.fleet_id to
   *  detect same-fleet vs different-fleet for the access predicate. */
  fleet_id?: string | null;
  /** Phase 9 — server-computed deriveChargerAccessMode
   *  classification: 'public' | 'fleet_only' | 'mixed'. The PWA's
   *  client-side accessibility resolver maps this + user mode +
   *  user fleet to a per-marker visual treatment. Optional only for
   *  back-compat with serializations from older API instances. */
  fleet_access_mode?: 'public' | 'fleet_only' | 'mixed';
}

export interface PublicLocationDetail extends PublicLocation {
  online_chargers?: number;
  /** Embedded per-stall list filtered to chargers THIS user can
   *  see. Eliminates the second round-trip to `GET /chargers`
   *  (tenant-scoped — would 404 for cross-tenant guests). Always
   *  non-empty in a successful response (the location-detail
   *  endpoint 404s when zero chargers are visible to the caller),
   *  except when private_only is true. */
  chargers?: PublicLocationDetailCharger[];
  /** The private switch (Warren, 2026-10-22): every stall this driver
   *  could see here is a private charger they may not start, so the list
   *  is empty and the station page says "This is a private charger".
   *  Absent from an older Api. */
  private_only?: boolean;
}
export async function fetchPublicLocation(id: string): Promise<PublicLocationDetail> {
  const res = await api
    .get(`locations/driver/${id}`)
    .json<ApiResponse<PublicLocationDetail>>();
  return res.data;
}

// ── My EV: personal vehicles (driver-owned, outside any fleet) ───────────────
// CRUD against the driver-scoped /me/vehicles route. Every call is owner-scoped
// on the server, so these only ever return or touch the caller's own vehicles.

export type VehicleConnector = 'Type2' | 'CCS' | 'CHAdeMO';

export interface Vehicle {
  _id: string;
  make: string;
  model: string;
  year: number;
  connector_standard: VehicleConnector;
  label: string | null;
  vin: string | null;
  battery_capacity_kwh: number | null;
  max_charge_power_kw: number | null;
  max_charge_current_a: number;
  onboard_phases: number;
  created_at: string;
  updated_at: string;
}

export interface CreateVehicleBody {
  make: string;
  model: string;
  year: number;
  connector_standard: VehicleConnector;
  label?: string;
  vin?: string;
  battery_capacity_kwh?: number;
  max_charge_power_kw?: number;
}

export interface UpdateVehicleBody {
  make?: string;
  model?: string;
  year?: number;
  connector_standard?: VehicleConnector;
  label?: string | null;
  vin?: string | null;
  battery_capacity_kwh?: number | null;
  max_charge_power_kw?: number | null;
}

export async function fetchMyVehicles(): Promise<Vehicle[]> {
  const res = await api.get('me/vehicles').json<ApiResponse<Vehicle[]>>();
  return res.data;
}

export async function createMyVehicle(body: CreateVehicleBody): Promise<Vehicle> {
  const res = await api.post('me/vehicles', { json: body }).json<ApiResponse<Vehicle>>();
  return res.data;
}

export async function updateMyVehicle(id: string, body: UpdateVehicleBody): Promise<Vehicle> {
  const res = await api.patch(`me/vehicles/${id}`, { json: body }).json<ApiResponse<Vehicle>>();
  return res.data;
}

export async function deleteMyVehicle(id: string): Promise<void> {
  await api.delete(`me/vehicles/${id}`).json<ApiResponse<{ deleted: boolean }>>();
}

// ── My Home (residential home setup) ─────────────────────────────────────
// Surfaces the /me/home routes. A home is a headless tenant the driver owns
// with one hidden residential Location. GET returns nulls when the driver has
// no home yet; POST creates it (409 when one already exists).
export interface HomeTenantView {
  _id: string;
  name: string;
  /** Informational home electricity rate (cents per kWh), owner-entered, or null
   *  when unset. Display-only; never a billing rate. Under time_of_use this is
   *  the base rate for hours outside every window. */
  owner_utility_rate_cents_per_kwh: number | null;
  /** Rate structure (flat / time_of_use / tiered); estimation math lives in
   *  lib/homeRate.ts. Display-only like the base rate. */
  owner_utility_rate_type: HomeRateType;
  owner_utility_tou_windows: HomeTouWindow[] | null;
  owner_utility_tiers: HomeRateTier[] | null;
}
export interface HomeAddress {
  street: string;
  city: string;
  state: string;
  zip: string;
  /** Stored full name ("United States", "Canada"). Absent on a home whose
   *  state never named either country. */
  country?: string;
  coordinates?: { lat: number; lng: number };
}
export interface HomeLocationView {
  _id: string;
  name: string;
  type: string;
  is_visible_on_driver_map: boolean;
  /** Populates the Home Settings address form. Null on pre-slice payloads. */
  address?: HomeAddress | null;
  /** IANA zone the home's guest windows mean; null when not set. Absent
   *  from an Api older than 2026-10-02. */
  timezone?: string | null;
  /** True once a change of country would get 409 HOME_COUNTRY_LOCKED (a
   *  charger or a payout account). Owners and managers only; absent for a
   *  viewer and from an older Api. */
  country_locked?: boolean;
  /** The home's currency, 'USD' or 'CAD' (item 3): what its prices and
   *  estimates are in. Null when the address settles no country; absent
   *  from an older Api (read as USD). */
  currency?: string | null;
}
export type HomeRole = 'owner' | 'manager' | 'viewer';

export interface MyHome {
  tenant: HomeTenantView | null;
  location: HomeLocationView | null;
  /** Caller's relationship to this home (household slice). Absent/null on
   *  pre-slice payloads and when the caller has no home — treat missing
   *  as 'owner' when tenant is set, for back-compat. */
  role?: HomeRole | null;
  /** Home charger summary, present for every role so member clients can
   *  render the home surfaces without the owner-scoped /my-chargers. */
  charger?: { _id: string; identity: string; display_name: string | null } | null;
}
export interface CreateHomeBody {
  /** Optional home nickname; the route defaults it to "My Home" when omitted. */
  name?: string;
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
    /** From the country picker. The Api checks the state and postal code
     *  against it; without one it derives the country from the state. */
    country?: HomeCountry;
  };
  /** The phone's zone, or the address zone the host chose instead. Left
   *  out, the Api uses the address zone when the state settles one. */
  timezone?: string;
  /** What `timezone` was based on, for the Api's audit row only. */
  timezone_source?: 'device' | 'address' | 'host';
}

export async function fetchMyHome(): Promise<MyHome> {
  const res = await api.get('me/home').json<ApiResponse<MyHome>>();
  return res.data;
}

export async function createMyHome(body: CreateHomeBody): Promise<MyHome> {
  const res = await api.post('me/home', { json: body }).json<ApiResponse<MyHome>>();
  return res.data;
}

// ── Home whitelist by email (Residential slice C) ────────────────────────

/** Send a whitelist invite by email. The response is identical whether or
 *  not the address has an account, so existence never leaks; an existing
 *  user is added directly, an unknown address gets a join-link email. */
export async function sendWhitelistInvite(email: string): Promise<{ sent: boolean }> {
  const res = await api
    .post('me/home/whitelist-invite', { json: { email } })
    .json<ApiResponse<{ sent: boolean }>>();
  return res.data;
}

export interface WhitelistInviteRow {
  _id: string;
  invited_email: string;
  created_at: string;
  expires_at: string;
}

export async function fetchWhitelistInvites(): Promise<WhitelistInviteRow[]> {
  const res = await api
    .get('me/home/whitelist-invites')
    .json<ApiResponse<WhitelistInviteRow[]>>();
  return res.data;
}

export async function revokeWhitelistInvite(id: string): Promise<void> {
  await api.delete(`me/home/whitelist-invite/${id}`).json<ApiResponse<{ revoked: boolean }>>();
}

// ── Home whitelist, home-scoped (Residential household slice) ────────────
// Read/write the pass list through /me/home so household MANAGERS work
// too (the /my-chargers PATCH stays owner-only). Names are hydrated
// server-side — pass-list entries aren't necessarily the viewer's friends.

export interface HomeWhitelistView {
  members: Array<{ user_id: string; name: string | null; email: string | null }>;
  cap: number;
  can_edit: boolean;
}

export async function fetchHomeWhitelist(): Promise<HomeWhitelistView> {
  const res = await api.get('me/home/whitelist').json<ApiResponse<HomeWhitelistView>>();
  return res.data;
}

/** Incremental pass-list edit (exactly one op per call). Incremental on
 *  purpose: invite-link accepts add members server-side at any moment,
 *  so a full-array replace from a stale snapshot could wipe them. */
export async function updateHomeWhitelist(
  ops: { add?: string[]; remove?: string[] },
): Promise<void> {
  const body = ops.add
    ? { add_user_ids: ops.add }
    : { remove_user_ids: ops.remove ?? [] };
  await api
    .patch('me/home/whitelist', { json: body })
    .json<ApiResponse<{ user_ids: string[] }>>();
}

// ── Home invite link (Residential household slice) ───────────────────────
// One active multi-use link per home. The raw URL is returned exactly
// once at mint (hash-at-rest server-side); minting again rotates.

export interface HomeInviteLinkMeta {
  active: boolean;
  created_at?: string;
  expires_at?: string;
  use_count?: number;
}

export async function fetchHomeInviteLink(): Promise<HomeInviteLinkMeta> {
  const res = await api.get('me/home/invite-link').json<ApiResponse<HomeInviteLinkMeta>>();
  return res.data;
}

export async function createHomeInviteLink(): Promise<{ url: string; expires_at: string }> {
  const res = await api
    .post('me/home/invite-link')
    .json<ApiResponse<{ url: string; expires_at: string }>>();
  return res.data;
}

export async function revokeHomeInviteLink(): Promise<void> {
  await api.delete('me/home/invite-link').json<ApiResponse<{ revoked: boolean }>>();
}

export interface HomeInviteAcceptResult {
  joined: boolean;
  already?: boolean;
  home_name: string;
  owner_name: string;
}

/** Accept a home invite link (any signed-in driver): free pass list +
 *  household viewer + auto-friend with the owner, in one call. Idempotent
 *  — re-accepting reports already: true. */
export async function acceptHomeInvite(token: string): Promise<HomeInviteAcceptResult> {
  const res = await api
    .post('home-invite/accept', { json: { token } })
    .json<ApiResponse<HomeInviteAcceptResult>>();
  return res.data;
}

// ── Household members (Residential household slice) ──────────────────────

export interface HouseholdMemberRow {
  user_id: string;
  name: string | null;
  email: string | null;
  role: 'manager' | 'viewer';
  joined_via: 'invite_link' | 'owner_add';
  created_at: string;
}

export async function fetchHousehold(): Promise<HouseholdMemberRow[]> {
  const res = await api.get('me/home/household').json<ApiResponse<HouseholdMemberRow[]>>();
  return res.data;
}

export async function setHouseholdRole(
  userId: string,
  role: 'manager' | 'viewer',
): Promise<void> {
  await api
    .patch(`me/home/household/${userId}`, { json: { role } })
    .json<ApiResponse<{ role: string }>>();
}

export async function removeHouseholdMember(userId: string): Promise<void> {
  await api.delete(`me/home/household/${userId}`).json<ApiResponse<{ removed: boolean }>>();
}

/** Rename the caller's home, edit its address and/or set its time zone.
 *  Owner or household manager server-side; a name change updates the home
 *  tenant and its Location together, an address change re-geocodes
 *  best-effort. `timezone` alone is a valid body. A country change is a 409
 *  HOME_COUNTRY_LOCKED once the home has a charger or a payout account. */
export async function updateMyHome(body: {
  name?: string;
  address?: { street: string; city: string; state: string; zip: string; country?: HomeCountry };
  timezone?: string;
}): Promise<{ name: string; address: HomeAddress | null; timezone?: string | null }> {
  const res = await api
    .patch('me/home', { json: body })
    .json<ApiResponse<{ name: string; address: HomeAddress | null; timezone?: string | null }>>();
  return res.data;
}

/** PATCH /me/home/rate body: the base rate plus (optionally) the structure.
 *  Fields absent from the body keep their stored values, so saving one mode
 *  never wipes the other mode's config. */
export interface HomeRateSaveBody {
  rate_type: HomeRateType;
  rate_cents_per_kwh: number | null;
  tou_windows?: HomeTouWindow[];
  tiers?: HomeRateTier[];
  /** The owner confirmed a flat per-kWh rate above 40 cents a second time (Warren, 21 October 2026, night). */
  confirm_above_40?: boolean;
}

export interface HomeRateView {
  owner_utility_rate_cents_per_kwh: number | null;
  owner_utility_rate_type: HomeRateType;
  owner_utility_tou_windows: HomeTouWindow[] | null;
  owner_utility_tiers: HomeRateTier[] | null;
}

/** Save the caller's INFORMATIONAL home electricity rate — flat value,
 *  time-of-use windows, or tiered monthly brackets. Owner-gated server-side.
 *  Display-only: this never becomes a charge, and the home charger stays free. */
export async function setMyHomeRate(body: HomeRateSaveBody): Promise<HomeRateView> {
  const res = await api
    .patch('me/home/rate', { json: body })
    .json<ApiResponse<HomeRateView>>();
  return res.data;
}

/**
 * Tariff fetch. The backend route is tenant-scoped + requireUser; no
 * public listing exists. Returned shape mirrors the admin tariff.
 */
export interface PublicTariff {
  _id: string;
  name: string;
  type: 'flat' | 'time_of_use' | 'tiered';
  currency: string;
  rates: {
    energy_rate: number; // cents/kWh
    time_rate?: number; // cents/min idle
    idle_grace_minutes?: number | null; // minutes free after charging (5-30)
    idle_max_charge_cents?: number | null;
    // cents flat session fee. On the start-preview tariff the server
    // rewrites it to EVI's connection fee; read connectionFeeCents there.
    flat_fee?: number;
    time_of_use_schedule?: Array<{
      day_of_week: number[];
      start_hour: number;
      end_hour: number;
      energy_rate: number;
    }>;
    tiered_schedule?: Array<{
      tier_max?: number | null;
      up_to_kwh?: number | null;
      energy_rate: number;
    }>;
  };
  tax_rate?: number;
  /** Per-tariff unit selector (Commit D). 'per_kwh' (default,
   *  historical) bills energy_kwh × rate. 'per_hour' bills
   *  duration_hours × rate. PreSessionPage's tariff display
   *  dispatches on this so the driver sees "$X/kWh" or "$X/hour"
   *  as appropriate. Optional — server defaults to per_kwh. */
  rate_type?: 'per_kwh' | 'per_hour';
}
export async function fetchPublicTariff(id: string): Promise<PublicTariff> {
  const res = await api.get(`tariffs/${id}`).json<ApiResponse<PublicTariff>>();
  return res.data;
}

// ── Driver-initiated remote start ───────────────────────────────────────────

export interface DriverStartResponse {
  commandId: string;
  acceptedAt: string;
  chargerResponse: unknown;
  /** The connector the Api started. Absent from an older Api. */
  connectorId?: number;
  /** Set when the Api started the other connector, where the car is plugged
   *  in, instead of the Available one the driver tapped (Warren, 29 Oct 2026).
   *  PreSessionPage says which plug it started. */
  connectorRedirect?: { fromConnectorId: number; toConnectorId: number };
  /** Set when the Api started another charger at the same site, where the car
   *  is plugged in, instead of the tapped one (wrong pedestal, Warren 29 Oct
   *  2026). PreSessionPage says which one, by the name on the unit. */
  chargerRedirect?: {
    fromChargerId: string; fromIdentity: string;
    toChargerId: string; toIdentity: string; toName: string; toConnectorId: number;
  };
  /** Set when the charger Rejected because it still held our own earlier start: the Api kept this start and resends
   *  it once after the charger's timeout (Warren, 29 Oct 2026). The driver waits; nothing failed. */
  startQueued?: { resendAt: string; waitSeconds: number; reason: string };
}

export interface DriverStartError {
  error: string;
  code: string;
  hint?: string;
  /** Why the refusal, beside its code, when the server says (item 6:
   *  'PRICE_NOT_SET' on a GUEST_ACCESS_DISABLED 403). */
  detail?: string;
  commandId?: string;
  /** Wallet-gate 402 responses carry cents-denominated balance info. */
  data?: {
    effectiveBalanceCents?: number;
    requiredCents?: number;
    shortfallCents?: number;
    /** Existing server decline details. Used only for driver-facing copy. */
    stripeErrorCode?: string | null;
    stripeDeclineCode?: string | null;
    /** Where the saved card is added again (detail CARD_NEEDS_READD, 29 Oct 2026). */
    readdCardPath?: string;
  };
}

/**
 * Start a charging session from the driver PWA.
 *
 * Hits the dedicated /chargers/:id/driver-start endpoint (admin
 * /commands/:action gates on `charger:command` which drivers don't
 * have). Server enforces tenant match, online state, wallet gate,
 * then dispatches RemoteStartTransaction.
 *
 * Throws a `DriverStartError`-shaped object (not a plain Error) so
 * the caller can render wallet-gate shortfalls + offline states
 * without string-matching.
 */
// ── Driver session stop / cancel (C0 charging-flow correctness) ─────
//
// Two endpoints; pick the one keyed on what the PWA has at hand:
//   - stopDriverSession(sessionId): caller has Session._id from the
//     active-session query. Server branches on status (pending →
//     cancel pre-auth + mark stopped; active → dispatch
//     RemoteStopTransaction; other → no-op success).
//   - cancelDriverChargerSession(identity): race fallback for the
//     "Waiting for charger" state where the polled query hasn't yet
//     primed a Session._id. Server looks up the caller's most-
//     recent pending/active session on this charger within a 5min
//     window and stops it. Returns action='no_session_found' when
//     nothing matches (the PWA can navigate away cleanly).
//
// Both return the action taken so the consumer can pick UX (e.g.
// don't show "Stopping charger…" toast for the no-op case).

export type DriverStopAction =
  | 'cancelled_pending'
  | 'remote_stop_dispatched'
  | 'no_session_found';

export async function stopDriverSession(
  sessionId: string,
): Promise<{ action: DriverStopAction }> {
  const res = await api.post(`sessions/${sessionId}/stop`, { json: {} })
    .json<{ success: boolean; data?: { action: DriverStopAction }; error?: { message?: string } }>();
  if (!res.success || !res.data) {
    throw new Error(res.error?.message ?? 'Stop session failed');
  }
  return res.data;
}

export async function cancelDriverChargerSession(
  chargerIdentity: string,
): Promise<{ action: DriverStopAction }> {
  const res = await api.post(`chargers/${chargerIdentity}/driver-cancel`, { json: {} })
    .json<{ success: boolean; data?: { action: DriverStopAction }; error?: { message?: string } }>();
  if (!res.success || !res.data) {
    throw new Error(res.error?.message ?? 'Cancel session failed');
  }
  return res.data;
}

export async function fetchStartRecovery(id: string): Promise<{ eligible: boolean }> {
  const res = await api.get(`sessions/${id}/start-recovery`).json<{ data: { eligible: boolean } }>();
  return res.data;
}

export async function startDriverCharger(
  idOrIdentity: string,
  /** payment_source: the checked option, for this start only (the Api never
   *  saves it; 2026-10-28). Omitted: the stored choice pays. */
  body: { connector_id?: number; payment_source?: 'card' | 'wallet'; recovery_session_id?: string; recovery_confirmed?: boolean } = {},
): Promise<DriverStartResponse> {
  const res = await api.post(`chargers/${idOrIdentity}/driver-start`, {
    json: body,
    timeout: 60_000,
    throwHttpErrors: false,
  }).json<{ success: boolean; data?: DriverStartResponse } & Partial<DriverStartError>>();

  if (!res.success || !res.data) {
    const err: DriverStartError = {
      error: res.error ?? 'Start failed',
      code: res.code ?? 'UNKNOWN',
      ...(res.hint ? { hint: res.hint } : {}),
      ...(res.detail ? { detail: res.detail } : {}),
      ...(res.commandId ? { commandId: res.commandId } : {}),
      ...(res.data ? { data: res.data } : {}),
    };
    // Intentionally throwing the structured DriverStartError object
    // (not an Error subclass) so callers can read `.code` / `.data`
    // without unwrapping — the catch site narrows the type.
    throw err;
  }
  return res.data;
}

export interface NudgeChargerResult {
  /** A live OCPP websocket is (was) still open server-side. */
  connected: boolean;
  /** At least one TriggerMessage landed (only meaningful when connected). */
  dispatched: boolean;
  connected_at?: string | null;
  ocpp_version?: string | null;
}

/**
 * Homeowner reconnect rescue — nudge an offline home charger. Asks the
 * server whether a live OCPP websocket for this charger is still open
 * and, when it is, forces a re-announce so is_online flips back on and
 * the 10s poll picks it up. Returns the honest connection truth:
 * connected:false means there is nothing to nudge and the UI should
 * move to guided recovery rather than wait on a spinner that cannot
 * resolve.
 */
export async function nudgeCharger(idOrIdentity: string): Promise<NudgeChargerResult> {
  const res = await api.post(`chargers/${idOrIdentity}/nudge`, {
    throwHttpErrors: false,
  }).json<{ success: boolean; data?: NudgeChargerResult; error?: string }>();
  if (!res.success || !res.data) {
    throw new Error(res.error ?? 'Could not reach the charger.');
  }
  return res.data;
}

/**
 * A predictive-maintenance alert on a home charger the caller OWNS,
 * shaped for the driver-facing dialog: the charger's label + live
 * status ride along so EVII can greet the owner with what's wrong and
 * gate the Clear affordance on reconnection.
 */
export interface MyChargerAlert {
  alert_id: string;
  rule_id: string;
  rule_name: string;
  type: string;
  severity: 'info' | 'warning' | 'critical';
  status: string;
  message: string;
  detected_at: string;
  charger: {
    id: string;
    label: string;
    identity: string;
    is_online: boolean;
    is_residential: boolean;
  };
}

/** The caller's active charger alerts (their own home chargers). */
export async function fetchMyChargerAlerts(): Promise<MyChargerAlert[]> {
  const res = await api.get('me/charger-alerts')
    .json<{ success: boolean; data: MyChargerAlert[] }>();
  return res.data ?? [];
}

/**
 * Clear (resolve) a charger alert. The server holds this with a 409
 * while the charger is still offline — the caller surfaces that as
 * "not back yet, we're watching" rather than an error state.
 */
export async function clearChargerAlert(alertId: string): Promise<void> {
  const res = await api.post(`me/charger-alerts/${alertId}/clear`, {
    throwHttpErrors: false,
  }).json<{ success: boolean; error?: string }>();
  if (!res.success) throw new Error(res.error ?? 'Could not clear the alert yet.');
}

// ── Payment methods + Outstanding ────────────────────────────────────────────

export interface PaymentMethod {
  id: string;
  type: string;
  brand?: string;
  last4?: string;
  exp_month?: number;
  exp_year?: number;
  created: number;
}

export interface PaymentMethodsResponse {
  stripe_configured: boolean;
  default_payment_method: string | null;
  payment_methods: PaymentMethod[];
}

export async function fetchPaymentMethods(): Promise<PaymentMethodsResponse> {
  const res = await api.get('billing/payment-methods').json<ApiResponse<PaymentMethodsResponse>>();
  return res.data;
}

export async function createPaymentSetupIntent(): Promise<{ client_secret: string; customer_id: string }> {
  const res = await api.post('billing/payment-methods/setup-intent').json<ApiResponse<{ client_secret: string; customer_id: string }>>();
  return res.data;
}

export async function setDefaultPaymentMethod(id: string): Promise<void> {
  await api.post(`billing/payment-methods/${id}/default`);
}

export async function deletePaymentMethod(id: string): Promise<void> {
  await api.delete(`billing/payment-methods/${id}`);
}

export interface OutstandingResponse {
  blocks_charging?: boolean;
  outstanding_cents: number; unpaid_count: number; currency: string;
  entries: { session_id: string; amount_cents: number; currency: string; status: string; payable: boolean; kind?: string;
    action?: 'pay' | 'reconcile' | 'refresh'; reason?: string }[];
}
export async function fetchOutstanding(): Promise<OutstandingResponse> {
  const res = await api.get('billing/outstanding').json<ApiResponse<OutstandingResponse>>();
  return res.data;
}

export async function payOutstanding(sessionId: string, paymentMethodId?: string, kind?: 'idle') {
  const res = await api.post('billing/retry', { json: { session_id: sessionId, payment_method_id: paymentMethodId, kind } })
    .json<ApiResponse<{ status: string; client_secret: string | null }>>();
  return res.data;
}

// ── Notification prefs (PATCH /me on user) ───────────────────────────────────
// (No dedicated endpoint yet — stubbed; settings page can show it as read-only or
//  call an admin endpoint. For now, keep it client-side.)

// ── Favorites ────────────────────────────────────────────────────────────────

/** Load the authed driver's favourite stations, most-recent first. */
export async function fetchFavorites(): Promise<FavoritesList> {
  const res = await api.get('favorites').json<ApiResponse<FavoritesList>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

/** Fast is-favorited membership check for the HeartButton. */
export async function checkFavorite(stationId: string): Promise<boolean> {
  const res = await api
    .get(`favorites/check/${stationId}`)
    .json<ApiResponse<FavoriteCheckResponse>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data.favorited;
}

/** Add a station to the authed driver's favourites (idempotent). */
export async function addFavorite(stationId: string): Promise<FavoriteToggleResponse> {
  const res = await api
    .post(`favorites/${stationId}`)
    .json<ApiResponse<FavoriteToggleResponse>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

/** Remove a station from the authed driver's favourites (idempotent). */
export async function removeFavorite(stationId: string): Promise<FavoriteToggleResponse> {
  const res = await api
    .delete(`favorites/${stationId}`)
    .json<ApiResponse<FavoriteToggleResponse>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

// ── Coupons (driver) ─────────────────────────────────────────────────────────

export interface CouponsDriverPayload {
  available: CouponPublic[];
  claims: CouponClaimPublic[];
}

export async function fetchCoupons(): Promise<CouponsDriverPayload> {
  const res = await api.get('coupons').json<ApiResponse<CouponsDriverPayload>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

export async function claimCoupon(args: { couponId?: string; code?: string }): Promise<CouponClaimPublic> {
  const res = await api
    .post('coupons/claim', { json: args })
    .json<ApiResponse<{ claim: CouponClaimPublic }>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data.claim;
}

// ── Driver charging summary (history dashboard) ─────────────────────────────

/**
 * Aggregated totals + top locations for the caller's sessions in
 * an optional date range. Backed by GET /me/charging/summary
 * (apps/api/src/routes/me/charging-summary.ts). Cross-tenant: the
 * server scopes by user_id, so guest sessions at other buildings
 * count toward the caller's totals.
 *
 * Status filter (server-side): only billed-status sessions
 * (completed / stopped_by_user / stopped_by_system) contribute
 * to totals — cancelled / failed sessions never charged the
 * driver and would inflate \$ figures incorrectly for the tax /
 * expense use case the dashboard is built for.
 */
export interface ChargingSummary {
  totalKwh: number;
  totalCents: number;
  sessionCount: number;
  avgCostCents: number;
  avgDurationMinutes: number;
  /** Effective $/kWh in cents. 0 when totalKwh is 0. */
  costPerKwhCents: number;
  topLocations: Array<{
    name: string;
    kwh: number;
    sessionCount: number;
  }>;
}

export async function fetchChargingSummary(
  params: { from?: string | null; to?: string | null },
): Promise<ChargingSummary> {
  const searchParams = new URLSearchParams();
  if (params.from) searchParams.set('from', params.from);
  if (params.to) searchParams.set('to', params.to);
  const res = await api
    .get('me/charging/summary', { searchParams })
    .json<ApiResponse<ChargingSummary>>();
  if (!res.success) {
    throw new Error((res as unknown as ApiError).error.message);
  }
  return res.data;
}

/**
 * Owner-scoped throughput (session count + kWh delivered) time-series
 * for the MyChargerSessionsPage chart. Reads SessionModel directly so
 * it shows utilisation regardless of whether sessions were guest-
 * billed to the network or fleet-billed to another tenant. Sibling
 * to fetchMyChargerRevenueSeries but with two metrics per bucket.
 */
export interface MyChargerThroughputSeriesBucket {
  sessions: number;
  kwh: number;
}
export interface MyChargerThroughputSeries {
  buckets: MyChargerThroughputSeriesBucket[];
  window: '24h' | '7d' | '30d';
  totalSessions: number;
  totalKwh: number;
}

export async function fetchMyChargerThroughputSeries(
  chargerId: string,
  period: '24h' | '7d' | '30d',
): Promise<MyChargerThroughputSeries> {
  const searchParams = new URLSearchParams({ period });
  const res = await api
    .get(`my-chargers/${chargerId}/throughput-series`, { searchParams })
    .json<ApiResponse<MyChargerThroughputSeries>>();
  if (!res.success) {
    throw new Error((res as unknown as ApiError).error.message);
  }
  return res.data;
}

/**
 * Owner-scoped revenue time-series for the MyChargerEarningsPage
 * chart slot. Per-day owner earnings (cleared payouts) for one
 * resident-owned charger across a 7d or 30d window. Matches the
 * /my-chargers/:id/earnings card totals (same PayoutModel source,
 * same status filter).
 */
export interface MyChargerRevenueSeries {
  buckets: number[];
  window: '7d' | '30d';
  totalEarned: number;
  sessionCount: number;
  /** The currency of the series above (item 3); absent: USD. */
  currency?: string;
  /** Each currency's series, only when the window holds two. */
  by_currency?: Array<{ currency: string; buckets: number[]; totalEarned: number; sessionCount: number }>;
}

export async function fetchMyChargerRevenueSeries(
  chargerId: string,
  period: '7d' | '30d',
): Promise<MyChargerRevenueSeries> {
  const searchParams = new URLSearchParams({ period });
  const res = await api
    .get(`my-chargers/${chargerId}/revenue-series`, { searchParams })
    .json<ApiResponse<MyChargerRevenueSeries>>();
  if (!res.success) {
    throw new Error((res as unknown as ApiError).error.message);
  }
  return res.data;
}

/**
 * Driver-scoped revenue time-series for the HistoryPage chart slot.
 * Mirrors the admin operator-summary's `revenueLast24h` shape, but
 * scoped to one user's sessions. 7d returns 7 daily buckets; 30d
 * returns 30 daily buckets. No '24h' — driver hourly granularity
 * isn't useful at this scope.
 */
export interface MyChargingRevenueSeries {
  buckets: number[];
  window: '7d' | '30d';
  totalSpent: number;
  sessionCount: number;
}

export async function fetchMyChargingRevenueSeries(
  period: '7d' | '30d' | '90d' | '1y' | 'all' | 'custom',
  range?: { from?: string; to?: string },
): Promise<MyChargingRevenueSeries> {
  // The route buckets any window by span. An explicit from/to (which the
  // HistoryPage always has, including for custom) wins server-side; the
  // period string rides along for the echo + preset fallback.
  const searchParams = new URLSearchParams({ period });
  if (range?.from) searchParams.set('from', range.from);
  if (range?.to) searchParams.set('to', range.to);
  const res = await api
    .get('me/charging/revenue-series', { searchParams })
    .json<ApiResponse<MyChargingRevenueSeries>>();
  if (!res.success) {
    throw new Error((res as unknown as ApiError).error.message);
  }
  return res.data;
}

/**
 * Driver CSV export downloader.
 *
 * Fetches `/me/sessions/export.csv` with the (from, to) query
 * params, then triggers a browser download via Blob URL +
 * synthetic anchor click. Filename is read from the response's
 * `Content-Disposition` header — the server already builds it
 * via buildInvoiceFilename('driver', ...), so we don't reinvent
 * the slug here.
 *
 * Returns void; on failure the underlying ky call throws and
 * the caller is responsible for catching + surfacing a toast.
 */
export async function downloadSessionsCsv(
  params: { from?: string | null; to?: string | null },
): Promise<void> {
  const searchParams = new URLSearchParams();
  if (params.from) searchParams.set('from', params.from);
  if (params.to) searchParams.set('to', params.to);
  const res = await api.get('me/sessions/export.csv', { searchParams });
  await downloadResponse(res, `evi-sessions-${Date.now()}.csv`);
}

/**
 * Driver PDF report downloader. Mirrors the CSV downloader —
 * same blob + anchor + revoke pattern, different endpoint and
 * default-fallback filename. The optional `tz` lands on the
 * cover note inside the PDF; the server validates the IANA
 * shape before interpolating.
 */
export async function downloadSessionsReportPdf(
  params: { from?: string | null; to?: string | null; tz?: string | null },
): Promise<void> {
  const searchParams = new URLSearchParams();
  if (params.from) searchParams.set('from', params.from);
  if (params.to) searchParams.set('to', params.to);
  if (params.tz) searchParams.set('tz', params.tz);
  const res = await api.get('me/sessions/export.pdf', { searchParams });
  await downloadResponse(res, `evi-report-${Date.now()}.pdf`);
}

/**
 * Driver receipts-bundle downloader.
 *
 * POSTs the selected session ids to /me/sessions/receipts/bundle,
 * blobs the ZIP response, and triggers a synthetic download.
 * Returns the X-Missing-Receipts list (parsed from the response
 * header) so the caller can surface "N receipts couldn't be
 * downloaded" feedback after the file lands.
 *
 * The fetch is direct (not via ky) because ky doesn't expose a
 * clean way to read non-2xx response headers AND the API base
 * URL prefix needs the same treatment. We mirror ky's auth +
 * credentials behavior here: include the Bearer token from the
 * auth store, send credentials, and parse the JSON error body
 * shape on failure for parity with the rest of the API surface.
 */
export interface ReceiptsBundleResult {
  /** Session ids the server couldn't include (no receipt URL,
   *  fetch failed, or timeout). Empty array on full success. */
  missing: string[];
}
export async function downloadReceiptsBundle(
  sessionIds: string[],
): Promise<ReceiptsBundleResult> {
  const token = useAuthStore.getState().accessToken;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const url = BASE_URL.endsWith('/')
    ? `${BASE_URL}me/sessions/receipts/bundle`
    : `${BASE_URL}/me/sessions/receipts/bundle`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({ sessionIds }),
  });
  if (!response.ok) {
    // Try to surface the structured error message; fall back to
    // a generic "couldn't bundle" if the body isn't JSON.
    let message = `Receipts bundle failed (${response.status})`;
    try {
      const body = (await response.json()) as { error?: { message?: string } };
      if (body?.error?.message) message = body.error.message;
    } catch { /* non-JSON body — keep the default message */ }
    throw new Error(message);
  }
  // Read the missing-receipts header BEFORE consuming the body
  // so a blob() failure doesn't strand the caller without the
  // missing list. Headers are read-anytime; body is one-shot.
  const missingHeader = response.headers.get('x-missing-receipts');
  const missing = missingHeader ? missingHeader.split(',').filter(Boolean) : [];
  await downloadResponse(response, `evi-receipts-${Date.now()}.zip`);
  return { missing };
}

/** Shared blob-download helper used by the CSV + PDF surfaces.
 *  Reads the server filename from Content-Disposition (defensive
 *  regex — accepts quoted or unquoted form), creates a Blob URL,
 *  triggers a synthetic anchor click, and revokes the URL in
 *  finally to avoid the slow leak that hits PWAs persisting
 *  across pages. */
async function downloadResponse(res: Response, fallbackFilename: string): Promise<void> {
  const cd = res.headers.get('content-disposition') ?? '';
  const filenameMatch = /filename\s*=\s*"?([^";]+)"?/i.exec(cd);
  const filename = filenameMatch?.[1] ?? fallbackFilename;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Defer the revoke so the download isn't cancelled on Safari/iOS, which
  // needs the blob URL to outlive the synchronous click (matches the
  // pattern in receipt-pdf.ts). Revoking in a finally block broke the CSV
  // export, PDF report, and receipts ZIP on those browsers.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── Support tickets ──────────────────────────────────────────────────────────

export type SupportTicketCategory = 'charge_start' | 'billing' | 'account' | 'other';

export interface CreateSupportTicketArgs {
  subject: string;
  description: string;
  category?: SupportTicketCategory;
  context?: {
    charger_identity?: string;
    location_id?: string;
    session_id?: string;
  };
  source?: 'driver_assist' | 'manual';
  /** Cloudflare Turnstile token from the support form's widget.
   *  Required since C4 — server rejects with 403 TURNSTILE_FAILED
   *  on missing/invalid/expired tokens. */
  turnstile_token: string;
}

export interface CreateSupportTicketResponse {
  ticket_id: string;
  created_at: string;
}

/** POST /me/support — Phase 1 of the driver-side support system.
 *  Schema-validated on the server (subject 1-200, description 1-5000).
 *  Returns the new ticket's id + created_at on success; throws via
 *  ky's HTTPError on validation or server failure (PWA catches and
 *  surfaces the project-envelope error.message). */
export async function createSupportTicket(
  args: CreateSupportTicketArgs,
): Promise<CreateSupportTicketResponse> {
  const res = await api
    .post('me/support', { json: args })
    .json<ApiResponse<CreateSupportTicketResponse>>();
  return res.data;
}

// ── Store (in-app catalog) ────────────────────────────────────────────────────

export async function fetchStoreCatalog(): Promise<StorePublicProduct[]> {
  const res = await api.get('store/catalog').json<ApiResponse<StorePublicProduct[]>>();
  return res.data;
}

/** One in-app product by its slug, a previous slug or its database id; the Api takes any of them. */
export async function fetchStoreProduct(param: string): Promise<StorePublicProduct> {
  const res = await api.get(`store/product/${param}`).json<ApiResponse<StorePublicProduct>>();
  return res.data;
}

/**
 * Start Stripe Checkout for the cart. The server re-fetches every product
 * and computes all prices from the DB; this sends ids + quantities only.
 * Returns the hosted Checkout URL to redirect to.
 */
export async function createStoreCheckout(
  items: Array<{ product_id: string; quantity: number }>,
): Promise<{ url: string; session_id: string }> {
  const res = await api
    .post('store/checkout', { json: { items } })
    .json<ApiResponse<{ url: string; session_id: string }>>();
  return res.data;
}

/** Best-effort server snapshot of the signed-in driver's cart, for the
 *  abandonment sweep. Fire-and-forget: callers swallow errors, and the
 *  snapshot is display-only (checkout still re-prices from the DB). */
export async function putCartSnapshot(
  items: Array<{ product_id: string; part_number: string; name: string; unit_price_cents: number; quantity: number }>,
): Promise<void> {
  await api.put('me/cart-snapshot', { json: { items } }).json<ApiResponse<{ ok: boolean }>>();
}

/** Drop the signed-in driver's server cart snapshot (cart emptied). */
export async function deleteCartSnapshot(): Promise<void> {
  await api.delete('me/cart-snapshot').json<ApiResponse<{ ok: boolean }>>();
}

/** Request a freight quote for a bulk cart (4 or more charger units). Records
 *  a bulk_quote lead and emails ops; no charge. */
export async function submitBulkQuote(
  items: Array<{ product_id: string; quantity: number }>,
  turnstileToken?: string,
): Promise<void> {
  await api
    .post('store/bulk-quote', {
      json: { items, ...(turnstileToken ? { turnstile_token: turnstileToken } : {}) },
    })
    .json<ApiResponse<{ ok: boolean }>>();
}

/** Program lead capture (today: the Sales Partner email list on the
 *  electrician ICP page, the residential upgrade sheet). Neutral
 *  response by design — success tells the caller nothing about
 *  whether the address was already listed. The Turnstile token is
 *  optional: keyless builds omit it and the server's fail-open
 *  policy accepts while no secret is configured. */
export async function submitStoreLead(
  email: string,
  source: string,
  turnstileToken?: string,
): Promise<void> {
  await api.post('store/leads', {
    json: {
      email,
      source,
      ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
    },
  }).json<ApiResponse<{ ok: boolean }>>();
}

export interface StoreCheckoutConfirmation {
  paid: boolean;
  payment_status?: string;
  order_number?: string;
  total_cents?: number;
  currency?: string;
}

/** Confirm a Checkout Session paid; the server records the Order
 *  idempotently (same creator the store webhook uses). */
export async function confirmStoreCheckout(sessionId: string): Promise<StoreCheckoutConfirmation> {
  const res = await api
    .get('store/checkout/confirm', { searchParams: { session_id: sessionId } })
    .json<ApiResponse<StoreCheckoutConfirmation>>();
  return res.data;
}

/** Order lifecycle as the server reports it. paid → processing →
 *  shipped → delivered is the buy-now path; quote_requested /
 *  confirmed / provisioning belong to the quote motion. */
export type MyStoreOrderStatus =
  | 'pending' | 'paid' | 'processing' | 'shipped' | 'delivered'
  | 'quote_requested' | 'confirmed' | 'provisioning' | 'cancelled';

export interface MyStoreOrder {
  id: string;
  order_number: string;
  status: MyStoreOrderStatus;
  total_cents: number;
  created_at: string;
  items: Array<{ name: string; quantity: number }>;
  tracking_number: string | null;
  tracking_carrier: string | null;
}

/** GET /store/orders/mine — the signed-in buyer's own orders, newest
 *  first. Owner-scoped list: the server filters on the caller's user
 *  id, so there are no params to pass. */
export async function fetchMyOrders(): Promise<MyStoreOrder[]> {
  const res = await api.get('store/orders/mine').json<ApiResponse<MyStoreOrder[]>>();
  return res.data;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Cents in `currency`, two decimals, in the app language (item 3). The
 *  same formatter as lib/money.ts, so a session reads alike on Session
 *  complete, Session detail and History: "CA$12.34" in English, "12,34
 *  CAD" in Spanish. English output is what the fixed 'en-US' gave. */
export function formatCents(cents: number, currency = 'USD'): string {
  return formatMoney(cents, currency, i18next.language);
}

export function buildAvatarUrl(style: AvatarStyle, seed: string): string {
  return `https://api.dicebear.com/7.x/${style}/svg?seed=${encodeURIComponent(seed)}`;
}

// ── Autocharge (plug & charge by vehicle id) ─────────────────────────────────
// docs/tickets/ocpp-autocharge-vid.md. The client never sees a vehicle id:
// the claim sends the session id and the server re-derives the car from the
// charger log. Non-2xx answers carry a typed message worth showing (409 for
// "already linked" / "two cars at once" / "paused for this model"), so these
// bindings read the body instead of surfacing ky's generic HTTPError.

export interface AutochargeCredential {
  _id: string;
  label: string | null;
  is_active: boolean;
  /** Inactive because of a model-level rule, not the driver's own revoke. */
  suspended: boolean;
  /** Linked from a no-grant session; goes live on the car's next plug-in this driver starts. */
  pending: boolean;
  /** Switched off by EVI support; only support can turn it back on. */
  disabled_by_admin?: boolean;
  vehicle: { _id: string; make: string; model: string; year: number } | null;
  created_at: string;
}

async function autochargeError(err: unknown, fallback: string): Promise<Error> {
  const resp = (err as { response?: Response }).response;
  if (resp) {
    const body = (await resp.json().catch(() => null)) as ApiError | null;
    if (body?.error?.message) return new Error(body.error.message);
  }
  return err instanceof Error ? err : new Error(fallback);
}

export async function claimAutocharge(
  body: { session_id: string; vehicle_id?: string; label?: string },
): Promise<{ card_id: string; status: 'active' | 'pending' }> {
  try {
    const res = await api.post('me/autocharge/claim', { json: body })
      .json<ApiResponse<{ card_id: string; status: 'active' | 'pending' }>>();
    if (!res.success) throw new Error((res as unknown as ApiError).error.message);
    return res.data;
  } catch (err) {
    throw await autochargeError(err, 'Could not link this car');
  }
}

export async function dismissAutocharge(sessionId: string): Promise<void> {
  await api.post('me/autocharge/dismiss', { json: { session_id: sessionId } }).json<ApiResponse<{ ok: true }>>();
}

export async function fetchAutochargeCredentials(): Promise<AutochargeCredential[]> {
  const res = await api.get('me/autocharge/credentials').json<ApiResponse<AutochargeCredential[]>>();
  if (!res.success) throw new Error((res as unknown as ApiError).error.message);
  return res.data;
}

export async function revokeAutocharge(id: string): Promise<void> {
  try {
    await api.delete(`me/autocharge/credentials/${id}`).json<ApiResponse<{ ok: true }>>();
  } catch (err) {
    throw await autochargeError(err, 'Could not unlink this car');
  }
}
